/**
 * Power Management Panel - Star Citizen 4.5 "Dawn of Engineering"
 * 
 * ARCHITECTURE (from POWER-PIP-SYSTEM-RESEARCH.md):
 * - POWER column on LEFT shows AVAILABLE pips from power plant
 * - Weapons = 1 SHARED pool (FixedPowerPool poolSize)  
 * - Shields = 1 SHARED pool (sum of all shield generators' powerUnits)
 * - Coolers = SEPARATE columns per cooler (each cooler individual)
 * - Quantum Drive = NOT pip-based (continuous power, grayed out in SCM)
 * 
 * SCM Mode: Weapons ON, Shields ON, Quantum OFF
 * NAV Mode: Weapons OFF, Shields OFF, Quantum ON
 * 
 * Pip counts come from each component's SPowerSegmentResourceUnit units
 * Min pips = minimumConsumptionFraction × units
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import type { Ship, ShipHardpoint } from '../../services/shipService';
import { componentService, ComponentItem } from '../../services/componentService';
import { 
  Crosshair, Navigation, Shield, Sparkles, Radio, Heart, Thermometer, Zap,
  ChevronDown, Check, RefreshCw, Gauge, Magnet, Pickaxe, Recycle, Cog
} from 'lucide-react';
import styles from './PowerManagementPanel.module.css';

// Helper to handle component loading errors gracefully (403 = Turnstile/bot protection)
const handleComponentLoadError = (err: any, componentType: string) => {
  if (err?.response?.status === 403) {
    console.warn(`${componentType} loading blocked (403) - refresh page if this persists`);
  } else {
    console.error(`Failed to load ${componentType}:`, err);
  }
};

interface PowerManagementPanelProps {
  ship: Ship;
  hardpoints: ShipHardpoint[];
  readOnly?: boolean;
  /** Callback when a component is swapped */
  onComponentChange?: (hardpointId: number, newItemId: number | null) => void;
  /** External overrides for hardpoints (swapped components) */
  hardpointOverrides?: Map<number, ComponentItem>;
  /** Callback when a loaded item (nested item like salvage head, mining laser, or module) is swapped */
  onLoadedItemChange?: (hardpointId: number, loadedItemId: number, newItemId: number | null) => void;
  /** External overrides for loaded items (nested swapped components) - key is "hardpointId:loadedItemId" */
  loadedItemOverrides?: Map<string, ComponentItem>;
  /** Callback to reset all component overrides */
  onResetAll?: () => void;
  /** Callback when power allocations change (for emissions panel reactivity) */
  onPowerAllocationsChange?: (allocations: PowerAllocation[]) => void;
  /** Optional: React node to render in the weapon details section */
  weaponContent?: React.ReactNode;
}

// Track which hardpoints have been swapped
export interface HardpointOverride {
  hardpointId: number;
  originalItemName: string;
  newItem: ComponentItem;
}

// Power allocation for emissions calculation - exported for EmissionsPanel
export interface PowerAllocation {
  id: string;
  type: 'weapon' | 'shield' | 'cooler' | 'radar' | 'lifesupport' | 'flightcontroller' | 'quantum' | 'power' | 'mining' | 'tractor' | 'salvage';
  allocatedPips: number;
  maxPips: number;
  minPips: number;
  isOn: boolean;
  /** Percentage of max power allocated (0 to 1) */
  percentage: number;
}

// Represents a single power-consuming component
interface PowerComponent {
  id: string;
  name: string;
  type: 'weapon' | 'shield' | 'cooler' | 'radar' | 'lifesupport' | 'flightcontroller' | 'quantum' | 'power' | 'mining' | 'tractor' | 'salvage';
  icon: React.ReactNode;
  maxPips: number;
  minPips: number;
  allocatedPips: number;
  isOn: boolean;
  hardpointId?: number;
  /** Size of the hardpoint slot (for component swapping) */
  slotSize?: number;
  /** Original item name (for identifying equipped item) */
  equippedItemName?: string;
  /** Localized display name of equipped item */
  equippedDisplayName?: string;
  /** Manufacturer of equipped item */
  equippedManufacturer?: string;
  /** Grade of equipped item (1=A, 2=B, 3=C, 4=D) */
  equippedGrade?: number;
  /** Only available in SCM mode (weapons, shields) */
  scmOnly?: boolean;
  /** Only available in NAV mode (quantum) */
  navOnly?: boolean;
  /** Utility mode - when ON, disables weapons */
  utilityMode?: boolean;
}

// Extended power plant info for display
interface PowerPlantInfo {
  hardpointId: number;
  slotSize: number;
  size: number; // Component size (S1-S9)
  itemName: string;
  displayName: string;
  manufacturer: string; // Parsed from description or fallback
  grade: number;
  subType: string; // Component subtype
  classification: string; // Parsed from description (Military, Civilian, Competition, etc.)
  powerUnits: number;
  powerOutput: number | null;
  description: string; // Raw description for parsing
  health: number; // Component hit points
}

// Extended shield info for display
interface ShieldInfo {
  hardpointId: number;
  slotSize: number;
  size: number; // Component size (S1-S9)
  itemName: string;
  displayName: string;
  manufacturer: string;
  grade: number;
  subType: string; // Component subtype
  classification: string;
  powerUnits: number;
  shieldHealthMax: number | null;
  regenRate: number | null;
  description: string;
  health: number; // Component hit points
}

// Extended cooler info for display
interface CoolerInfo {
  hardpointId: number;
  slotSize: number;
  size: number; // Component size (S1-S9)
  itemName: string;
  displayName: string;
  manufacturer: string;
  grade: number;
  subType: string; // Component subtype
  classification: string;
  powerUnits: number;
  coolingRate: number | null;
  description: string;
  health: number; // Component hit points
}

// Extended quantum drive info for display
interface QuantumDriveInfo {
  hardpointId: number;
  slotSize: number;
  size: number; // Component size (S1-S9)
  itemName: string;
  displayName: string;
  manufacturer: string;
  grade: number;
  subType: string; // Component subtype
  classification: string;
  driveSpeed: number | null;
  spoolUpTime: number | null;
  cooldownTime: number | null;
  fuelRequirement: number | null;
  description: string;
  health: number; // Component hit points
}

// Extended radar info for display
interface RadarInfo {
  hardpointId: number;
  slotSize: number;
  size: number; // Component size (S1-S9)
  itemName: string;
  displayName: string;
  manufacturer: string;
  grade: number;
  subType: string; // Component subtype
  classification: string;
  powerUnits: number;
  description: string;
  health: number; // Component hit points
  sensitivity: number; // Detection range multiplier (0-1)
  piercing: number; // Signal piercing capability (0-1)
}

// Extended utility equipment info for display (Mining, Tractor, Salvage)
interface UtilityEquipmentInfo {
  hardpointId: number;
  slotSize: number;
  itemName: string;
  displayName: string;
  manufacturer: string;
  grade: number;
  powerUnits: number;
  health: number;
  type: 'mining' | 'tractor' | 'salvage';
  // Mining laser stats
  miningStats?: {
    instability?: number | null;
    resistance?: number | null;
    optimalWindow?: number | null;
    power?: number | null;
    range?: number | null;
  };
  // Tractor beam stats
  tractorStats?: {
    range?: number | null;
    force?: number | null;
    volume?: number | null;
    cargoRange?: number | null;
    cargoForce?: number | null;
  };
  // Salvage stats (on the head)
  salvageStats?: {
    headCount?: number | null;
    armingDistance?: number | null;
    usesCargoGrid?: boolean | null;
  };
  // For salvage arms - info about the attached salvage head
  salvageHead?: {
    loadedItemId: number;  // The LoadedItem record ID (for swapping)
    itemId: number;        // The ItemDefinition ID
    itemName: string;
    displayName: string;
    size: number;
    manufacturer: string;
    moduleSlots?: number;  // Number of module attachment slots
  };
  // For mining turrets/arms - info about the attached mining laser
  miningLaser?: {
    loadedItemId: number;  // The LoadedItem record ID (for swapping)
    itemId: number;        // The ItemDefinition ID
    itemName: string;
    displayName: string;
    size: number;
    manufacturer: string;
    moduleSlots?: number;  // Number of module attachment slots (typically 3 for S1)
    // Stats for the mining laser
    instability?: number | null;
    resistance?: number | null;
    optimalWindow?: number | null;
    power?: number | null;
    range?: number | null;
  };
  // Modules/attachments on the head (mining modifiers, salvage modifiers like Abrade)
  modules?: {
    loadedItemId: number;  // The LoadedItem/ChildAttachment record ID (for swapping)
    itemId: number;        // The ItemDefinition ID
    itemName: string;
    displayName: string;
    size: number;
    manufacturer: string;
    type: string; // MiningModifier, SalvageModifier, etc.
    description?: string; // Item description with modifier stats
    tags?: string; // Item tags
    fullDef?: any; // Full item definition for accessing all properties
  }[];
}

/**
 * Build power components from ACTUAL hardpoints
 * - POWER column shows available pips (source)
 * - Shields = ONE shared pool (sum of all shield generators)
 * - Coolers = SEPARATE columns per cooler
 */
const buildPowerComponents = (
  ship: Ship, 
  hardpoints: ShipHardpoint[], 
  totalPips: number,
  hardpointOverrides?: Map<number, ComponentItem>
): PowerComponent[] => {
  const components: PowerComponent[] = [];
  
  // 1. WEAPONS - Single shared pool from ship.weaponPoolSize
  // NOTE: weaponPoolSize comes from P4K FixedPowerPool poolSize attribute
  // Only add if ship has weapon hardpoints (guns, turrets, missiles, etc.)
  const hasWeapons = hardpoints.some(h => 
    h.hardpointType === 'Weapon' ||
    h.hardpointType === 'WeaponGun' || 
    h.hardpointType === 'WeaponDefensive' ||
    h.hardpointType === 'TurretBase' ||
    h.hardpointType === 'MannedTurret' ||
    h.hardpointType === 'Turret' ||
    h.category === 'Weapons' ||
    h.category === 'Missiles'
  );
  
  if (hasWeapons) {
    const weaponPoolSize = ship.weaponPoolSize || 10; // Default to 10 (common for larger ships)
    console.log('=== WEAPONS DEBUG ===');
    console.log(`Weapon pool size from ship: ${ship.weaponPoolSize}, using: ${weaponPoolSize}`);
    components.push({
      id: 'weapons',
      name: 'WEAPONS',
      type: 'weapon',
      icon: <Crosshair size={18} />,
      maxPips: weaponPoolSize,
      minPips: 0, // Weapons can be completely off
      allocatedPips: 0, // Start at 0, will be set by calculateDefaultAllocation
      isOn: true,
      scmOnly: true
    });
  }
  
  // 2. ENGINES (Flight Controller)
  // Only add if ship has flight controller
  const flightController = hardpoints.find(h => 
    h.componentType?.toLowerCase().includes('flightcontroller') ||
    h.itemDefinition?.type?.toLowerCase().includes('flightcontroller')
  );
  
  if (flightController) {
    const fcUnits = flightController?.itemDefinition?.powerPipStats?.powerUnits || 4;
    // NOTE: Most flight controllers have minimumConsumptionFraction of 0.125 (1/8)
    // Database may have incorrect values, so we use 0.125 as default
    const fcMinFraction = flightController?.itemDefinition?.powerPipStats?.minimumConsumptionFraction || 0.125;
    const fcMinPips = Math.max(1, Math.round(fcUnits * fcMinFraction)); // At least 1 pip
    console.log('=== ENGINES DEBUG ===');
    console.log(`FlightController found: ${!!flightController}, name=${flightController?.itemDefinition?.localizedName || flightController?.defaultItemName}, units=${fcUnits}, minFraction=${fcMinFraction}, minPips=${fcMinPips}`);
    components.push({
      id: 'engines',
      name: 'ENGINES',
      type: 'flightcontroller',
      icon: <Navigation size={18} />,
      maxPips: fcUnits,
      minPips: fcMinPips,
      allocatedPips: fcMinPips, // Default to minimum
      isOn: true,
      hardpointId: flightController?.id
    });
  }
  
  // 3. SHIELDS - ONE SHARED pool (sum all shield generators' pips)
  // Only add if ship has shield hardpoints (check for slots, not just equipped shields)
  const allShieldHardpoints = hardpoints.filter(h => {
    const itemType = h.itemDefinition?.type?.toLowerCase() || '';
    return h.hardpointType === 'Shield' || itemType === 'shield' || itemType === 'shieldgenerator';
  });
  
  if (allShieldHardpoints.length > 0) {
    // Only count hardpoints that actually have a shield EQUIPPED (not empty slots)
    const shields = allShieldHardpoints.filter(h => {
      const hasItemEquipped = h.itemDefinition && h.itemDefinition.powerPipStats?.powerUnits;
      return hasItemEquipped;
    });
    
    const emptyShieldSlots = allShieldHardpoints.length - shields.length;
    
    let shieldMaxPips = 0;
    let shieldMinPipsTotal = 0;
    if (shields.length > 0) {
      console.log('=== SHIELD DEBUG ===');
      console.log(`Found ${shields.length} equipped shields (${emptyShieldSlots} empty slots)`);
      shields.forEach((shield, idx) => {
        // Check for override first
        const override = hardpointOverrides?.get(shield.id);
        const units = override?.powerPipStats?.powerUnits ?? shield.itemDefinition?.powerPipStats?.powerUnits ?? 2;
        const minFraction = override?.powerPipStats?.minimumConsumptionFraction ?? shield.itemDefinition?.powerPipStats?.minimumConsumptionFraction ?? 0.5;
        const shieldName = override?.localizedName ?? shield.itemDefinition?.localizedName ?? shield.defaultItemName;
        console.log(`Shield ${idx + 1}: hardpointId=${shield.id}, name=${shieldName}, units=${units}, minFraction=${minFraction}, minPips=${Math.round(units * minFraction)}`);
        shieldMaxPips += units;
        shieldMinPipsTotal += Math.round(units * minFraction);
      });
      console.log(`TOTAL SHIELDS: maxPips=${shieldMaxPips}, minPips=${shieldMinPipsTotal}`);
    } else {
      console.log('=== SHIELD DEBUG: No shields equipped, using defaults ===');
      shieldMaxPips = 4;
      shieldMinPipsTotal = 2;
    }
    
    components.push({
      id: 'shields',
      name: 'SHIELDS',
      type: 'shield',
      icon: <Shield size={18} />,
      maxPips: shieldMaxPips,
      minPips: shieldMinPipsTotal,
      allocatedPips: shieldMinPipsTotal, // Default to minimum
      isOn: true,
      scmOnly: true
    });
  }
  
  // 4. QUANTUM DRIVE - NOT pip-based in SCM, but ON in NAV mode
  // Only add if ship has quantum drive slot
  const quantumDrive = hardpoints.find(h => 
    h.hardpointType === 'QuantumDrive' ||
    h.componentType?.toLowerCase().includes('quantumdrive') ||
    h.itemDefinition?.type?.toLowerCase().includes('quantumdrive')
  );
  
  if (quantumDrive) {
    components.push({
      id: 'quantum',
      name: 'QUANTUM',
      type: 'quantum',
      icon: <Sparkles size={18} />,
      maxPips: 0, // No pips for quantum (continuous power)
      minPips: 0,
      allocatedPips: 0,
      isOn: false, // Off in SCM by default
      navOnly: true,
      hardpointId: quantumDrive.id
    });
  }
  
  // 5. RADAR
  // NOTE: Radar has minimumConsumptionFraction of 1.0 - it's always on at full power
  // You can't reduce radar power, it's either on or off
  // Only add if ship has radar slot
  const radar = hardpoints.find(h => 
    h.hardpointType === 'Radar' ||
    h.componentType?.toLowerCase().includes('radar') ||
    h.itemDefinition?.type?.toLowerCase() === 'radar'
  );
  
  if (radar) {
    // Check for radar override
    const radarOverride = hardpointOverrides?.get(radar.id);
    const effectiveRadar = radarOverride || radar?.itemDefinition;
    const radarUnits = effectiveRadar?.powerPipStats?.powerUnits || 3;
    // Radar is always-on at full power (minFraction = 1.0)
    const radarMinFraction = 1.0; // Radar cannot be throttled
    const radarMinPips = radarUnits; // min = max for radar
    console.log('=== RADAR DEBUG ===');
    console.log(`Radar found: ${!!radar}, override: ${!!radarOverride}, name=${effectiveRadar?.localizedName || radar?.defaultItemName}, units=${radarUnits}, minFraction=${radarMinFraction}, minPips=${radarMinPips}`);
    components.push({
      id: 'radar',
      name: 'RADAR',
      type: 'radar',
      icon: <Radio size={18} />,
      maxPips: radarUnits,
      minPips: radarMinPips,
      allocatedPips: radarMinPips, // Radar always at max (can't throttle)
      isOn: true,
      hardpointId: radar?.id
    });
  }
  
  // 6. LIFE SUPPORT
  // Only add if ship has life support slot
  const lifeSupport = hardpoints.find(h => 
    h.componentType?.toLowerCase().includes('lifesupport') ||
    h.itemDefinition?.type?.toLowerCase() === 'lifesupportgenerator' ||
    h.itemDefinition?.type?.toLowerCase().includes('lifesupport')
  );
  
  if (lifeSupport) {
    const lsUnits = lifeSupport?.itemDefinition?.powerPipStats?.powerUnits || 2;
    const lsMinFraction = lifeSupport?.itemDefinition?.powerPipStats?.minimumConsumptionFraction || 0.5;
    const lsMinPips = Math.round(lsUnits * lsMinFraction);
    console.log('=== LIFE SUPPORT DEBUG ===');
    console.log(`LifeSupport found: ${!!lifeSupport}, name=${lifeSupport?.itemDefinition?.localizedName || lifeSupport?.defaultItemName}, units=${lsUnits}, minFraction=${lsMinFraction}, minPips=${lsMinPips}`);
    components.push({
      id: 'lifesupport',
      name: 'LIFE SUPPORT',
      type: 'lifesupport',
      icon: <Heart size={18} />,
      maxPips: lsUnits,
      minPips: lsMinPips,
      allocatedPips: lsMinPips, // Default to minimum
      isOn: true,
      hardpointId: lifeSupport?.id
    });
  }
  
  // 7. COOLERS - SEPARATE columns per cooler
  // Each cooler should use its OWN item definition stats
  // If no stats available, use consistent defaults
  // Use simple hardpointType filter (same as ShipDetailV2)
  // Exclude "Controller_" items and controller hardpoints - these are system controllers, not actual coolers
  // Only add if ship has cooler slots
  const coolers = hardpoints.filter(h => 
    h.hardpointType === 'Cooler' && 
    !h.defaultItemName?.toLowerCase().startsWith('controller_') &&
    !h.slotName?.toLowerCase().includes('controller') &&
    h.itemDefinition?.powerPipStats?.powerUnits // Must have power pip data
  );
  
  // Default cooler stats (used when no powerPipStats available)
  const DEFAULT_COOLER_UNITS = 3;
  const DEFAULT_COOLER_MIN_FRACTION = 0.6666667; // 2/3 = 2 pips minimum for 3 pip cooler
  
  if (coolers.length > 0) {
    coolers.forEach((cooler, index) => {
      // Try to get this cooler's specific stats
      const pipStats = cooler.itemDefinition?.powerPipStats;
      const hasPipStats = pipStats?.powerUnits !== undefined;
      
      // Use this cooler's stats if available, otherwise use defaults
      const units: number = hasPipStats 
        ? (pipStats!.powerUnits ?? DEFAULT_COOLER_UNITS)
        : DEFAULT_COOLER_UNITS;
      const minFraction: number = hasPipStats && pipStats?.minimumConsumptionFraction !== undefined
        ? pipStats.minimumConsumptionFraction 
        : DEFAULT_COOLER_MIN_FRACTION;
      
      const minPips = Math.round(units * minFraction);

      components.push({
        id: `cooler-${cooler.id || index}`,
        name: coolers.length > 1 ? `COOLER ${index + 1}` : 'COOLER',
        type: 'cooler',
        icon: <Thermometer size={18} />,
        maxPips: units,
        minPips: minPips,
        allocatedPips: minPips, // Default to minimum
        isOn: true,
        hardpointId: cooler.id
      });
    });
  }
  
  // 8. UTILITY EQUIPMENT - Mining, Tractor Beams, Salvage
  // These are special modes that can overlay SCM/NAV
  // When active, they disable weapons but ship can still fly normally
  // They are OFF by default and must be toggled on
  
  // Helper to find mining equipment (WeaponMining in mining slots)
  const findMiningEquipment = (): { pips: number; name: string; hardpointId?: number }[] => {
    const equipment: { pips: number; name: string; hardpointId?: number }[] = [];
    
    hardpoints.forEach(hp => {
      const slotName = (hp.slotName || '').toLowerCase();
      const itemType = (hp.itemDefinition?.type || '').toLowerCase();
      const defaultName = (hp.defaultItemName || '').toLowerCase();
      
      // Skip controllers
      if (slotName.includes('controller') || defaultName.includes('controller')) return;
      
      // Skip ore pods - they are storage containers, not mining equipment (no pips)
      const isOrePod = slotName.includes('pod') || defaultName.includes('pod') || itemType === 'container';
      if (isOrePod) return;
      
      // Skip salvage slots - they're not mining!
      if (slotName.includes('salvage')) return;
      if (slotName.includes('salvage')) return;
      
      // Check for mining equipment - must be mining-related slot OR have WeaponMining item
      const isMiningItem = itemType === 'weaponmining';
      const isMiningSlot = slotName.includes('mining');
      const isUtilityTurret = itemType === 'utilityturret' && slotName.includes('mining');
      
      // For ToolArm, only consider if it's a mining slot
      const isToolArmMining = itemType === 'toolarm' && slotName.includes('mining');
      
      if (isMiningItem || isMiningSlot || isUtilityTurret || isToolArmMining) {
        // Check for override
        const override = hardpointOverrides?.get(hp.id);
        let miningItem = override || hp.itemDefinition;
        
        // For ToolArm/turrets, look for actual mining laser in loadedItems
        if (hp.loadedItems && hp.loadedItems.length > 0) {
          const findMiningLaser = (items: any[]): any | null => {
            for (const item of items) {
              const type = (item.itemDefinition?.type || '').toLowerCase();
              if (type === 'weaponmining') return item.itemDefinition;
              if (item.childAttachments?.length > 0) {
                const found = findMiningLaser(item.childAttachments);
                if (found) return found;
              }
            }
            return null;
          };
          const found = findMiningLaser(hp.loadedItems as any[]);
          if (found) miningItem = found;
        }
        
        // Only add if we found an actual mining laser (WeaponMining type)
        if (miningItem && (miningItem.type?.toLowerCase() === 'weaponmining' || isMiningSlot)) {
          const pips = miningItem?.powerPipStats?.powerUnits || 1;
          const name = miningItem?.localizedName || miningItem?.itemName || 'Mining Laser';
          equipment.push({ pips, name, hardpointId: hp.id });
        }
      }
    });
    
    return equipment;
  };
  
  // Helper to find tractor beams
  const findTractorBeams = (): { pips: number; name: string; hardpointId?: number }[] => {
    const equipment: { pips: number; name: string; hardpointId?: number }[] = [];
    
    hardpoints.forEach(hp => {
      const slotName = (hp.slotName || '').toLowerCase();
      const itemType = (hp.itemDefinition?.type || '').toLowerCase();
      const defaultName = (hp.defaultItemName || '').toLowerCase();
      
      // Skip controllers
      if (slotName.includes('controller') || defaultName.includes('controller')) return;
      
      // Check for tractor beam
      if (itemType === 'tractorbeam' || slotName.includes('tractor') || defaultName.includes('tractor')) {
        const override = hardpointOverrides?.get(hp.id);
        const tractorItem = override || hp.itemDefinition;
        
        if (tractorItem && (tractorItem.type?.toLowerCase() === 'tractorbeam' || itemType === 'tractorbeam')) {
          const pips = tractorItem?.powerPipStats?.powerUnits || 1;
          const name = tractorItem?.localizedName || tractorItem?.itemName || 'Tractor Beam';
          equipment.push({ pips, name, hardpointId: hp.id });
        }
      }
    });
    
    return equipment;
  };
  
  // Helper to find salvage equipment
  const findSalvageEquipment = (): { pips: number; name: string; hardpointId?: number }[] => {
    const equipment: { pips: number; name: string; hardpointId?: number }[] = [];
    
    hardpoints.forEach(hp => {
      const slotName = (hp.slotName || '').toLowerCase();
      const itemType = (hp.itemDefinition?.type || '').toLowerCase();
      const defaultName = (hp.defaultItemName || '').toLowerCase();
      
      // Skip controllers
      if (slotName.includes('controller') || defaultName.includes('controller')) return;
      
      // Skip internal salvage components - they don't consume pips
      const isInternalSalvage = defaultName.includes('filler') || defaultName.includes('internalstorage') ||
                                defaultName.includes('disintegration') || defaultName.includes('fieldsupporter') ||
                                defaultName.includes('buff_modifier') || itemType === 'container';
      if (isInternalSalvage) return;
      
      // Check for salvage head (the actual power-consuming equipment)
      if (itemType === 'salvagehead' || 
          (slotName.includes('salvage') && slotName.includes('arm')) ||
          (itemType === 'toolarm' && slotName.includes('salvage'))) {
        const override = hardpointOverrides?.get(hp.id);
        let salvageItem = override || hp.itemDefinition;
        
        // For ToolArm, look for actual salvage head in loadedItems
        if (hp.loadedItems && hp.loadedItems.length > 0) {
          const findSalvageHead = (items: any[]): any | null => {
            for (const item of items) {
              const type = (item.itemDefinition?.type || '').toLowerCase();
              if (type === 'salvagehead') return item.itemDefinition;
              if (item.childAttachments?.length > 0) {
                const found = findSalvageHead(item.childAttachments);
                if (found) return found;
              }
            }
            return null;
          };
          const found = findSalvageHead(hp.loadedItems as any[]);
          if (found) salvageItem = found;
        }
        
        if (salvageItem) {
          const pips = salvageItem?.powerPipStats?.powerUnits || 1;
          // Generate a better name for ToolArm salvage equipment
          let name = salvageItem?.localizedName || '';
          if (!name || name === 'Unknown Item') {
            if (slotName.includes('left')) {
              name = 'Salvage Arm (Left)';
            } else if (slotName.includes('right')) {
              name = 'Salvage Arm (Right)';
            } else {
              name = 'Salvage Arm';
            }
          }
          equipment.push({ pips, name, hardpointId: hp.id });
        }
      }
    });
    
    return equipment;
  };
  
  const miningEquipment = findMiningEquipment();
  const tractorBeams = findTractorBeams();
  const salvageEquipment = findSalvageEquipment();
  
  // Add MINING if ship has mining equipment
  if (miningEquipment.length > 0) {
    const totalMiningPips = miningEquipment.reduce((sum, e) => sum + e.pips, 0);
    console.log('=== MINING DEBUG ===');
    console.log(`Found ${miningEquipment.length} mining equipment, total pips: ${totalMiningPips}`);
    miningEquipment.forEach(e => console.log(`  - ${e.name}: ${e.pips} pips`));
    
    components.push({
      id: 'mining',
      name: miningEquipment.length > 1 ? `MINING (${miningEquipment.length})` : 'MINING',
      type: 'mining',
      icon: <Pickaxe size={18} />,
      maxPips: totalMiningPips,
      minPips: totalMiningPips, // Mining is all-or-nothing when active
      allocatedPips: 0, // OFF by default
      isOn: false, // Utility modes are OFF by default
      utilityMode: true // Disables weapons when active
    });
  }
  
  // Add TRACTOR if ship has tractor beams
  if (tractorBeams.length > 0) {
    const totalTractorPips = tractorBeams.reduce((sum, e) => sum + e.pips, 0);
    console.log('=== TRACTOR DEBUG ===');
    console.log(`Found ${tractorBeams.length} tractor beams, total pips: ${totalTractorPips}`);
    tractorBeams.forEach(e => console.log(`  - ${e.name}: ${e.pips} pips`));
    
    components.push({
      id: 'tractor',
      name: tractorBeams.length > 1 ? `TRACTOR (${tractorBeams.length})` : 'TRACTOR',
      type: 'tractor',
      icon: <Magnet size={18} />,
      maxPips: totalTractorPips,
      minPips: totalTractorPips, // Tractor is all-or-nothing when active
      allocatedPips: 0, // OFF by default
      isOn: false, // Utility modes are OFF by default
      utilityMode: true // Disables weapons when active
    });
  }
  
  // Add SALVAGE if ship has salvage equipment
  if (salvageEquipment.length > 0) {
    const totalSalvagePips = salvageEquipment.reduce((sum, e) => sum + e.pips, 0);
    
    components.push({
      id: 'salvage',
      name: salvageEquipment.length > 1 ? `SALVAGE (${salvageEquipment.length})` : 'SALVAGE',
      type: 'salvage',
      icon: <Recycle size={18} />,
      maxPips: totalSalvagePips,
      minPips: totalSalvagePips, // Salvage is all-or-nothing when active
      allocatedPips: 0, // OFF by default
      isOn: false, // Utility modes are OFF by default
      utilityMode: true // Disables weapons when active
    });
  }
  
  return components;
};

/**
 * Single vertical pip column for a component (consumers)
 */
const PipColumn: React.FC<{
  component: PowerComponent;
  isSelected: boolean;
  onSelect: () => void;
  onToggle: () => void;
  onPipChange: (pips: number) => void;
  disabled?: boolean;
  readOnly?: boolean;
  availablePips: number;
  isPowerColumn?: boolean;
  showSelectorButton?: boolean;
}> = ({ component, isSelected, onSelect, onToggle, onPipChange, disabled, readOnly, availablePips, isPowerColumn, showSelectorButton = true }) => {
  
  // Selector button that goes at the bottom of every column
  const selectorButton = showSelectorButton ? (
    <button
      className={`${styles.selectorButton} ${isPowerColumn ? styles.powerSelectorButton : ''} ${isSelected ? styles.selectorActive : ''} ${disabled ? styles.selectorDisabled : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      title={component.name}
    >
      {component.icon}
    </button>
  ) : null;

  // For quantum drive, show a special indicator sized to match pip height
  if (component.type === 'quantum') {
    // Quantum typically consumes equivalent of ~4 pips worth of continuous power
    const quantumPipEquivalent = 4;
    return (
      <div 
        className={`${styles.pipColumn} ${isSelected ? styles.pipColumnSelected : ''} ${disabled ? styles.pipColumnDisabled : ''}`}
        onClick={onSelect}
      >
        <div className={styles.pipColumnHeader}>
          <span className={`${styles.systemIcon} ${component.isOn && !disabled ? styles.systemIconOn : ''}`}>
            {component.icon}
          </span>
          <span className={styles.systemName}>{component.name}</span>
        </div>
        
        <div className={styles.pipColumnSpacer} />
        
        <div 
          className={`${styles.quantumIndicator} ${component.isOn && !disabled ? styles.quantumActive : ''}`}
          style={{ minHeight: `${quantumPipEquivalent * 18 + (quantumPipEquivalent - 1) * 4}px` }}
        >
          {component.isOn && !disabled ? 'READY' : 'OFFLINE'}
        </div>
        
        <button 
          className={`${styles.toggleButton} ${component.isOn ? styles.toggleOn : styles.toggleOff}`}
          onClick={(e) => {
            e.stopPropagation();
            if (!readOnly && !disabled) onToggle();
          }}
          disabled={readOnly || disabled}
        >
          {component.isOn ? 'ON' : 'OFF'}
        </button>
        
        {selectorButton}
      </div>
    );
  }
  
  // For power column with many pips, use multi-row layout
  if (isPowerColumn && component.maxPips > 10) {
    const numColumns = component.maxPips > 20 ? 3 : 2;
    const pipsPerColumn = Math.ceil(component.maxPips / numColumns);
    
    const columns: React.ReactNode[][] = Array.from({ length: numColumns }, () => []);
    
    for (let i = 0; i < component.maxPips; i++) {
      const columnIndex = Math.floor(i / pipsPerColumn);
      const isFilled = i < component.allocatedPips;
      
      columns[columnIndex].push(
        <div
          key={i}
          className={`
            ${styles.pip} 
            ${styles.pipSmall}
            ${isFilled ? styles.pipFilled : styles.pipEmpty} 
            ${styles.powerPip}
            ${!isFilled ? styles.pipAvailable : ''}
          `}
          title={isFilled ? 'Allocated pip' : 'Available pip'}
        />
      );
    }
    
    return (
      <div 
        className={`${styles.pipColumn} ${isSelected ? styles.pipColumnSelected : ''} ${styles.powerColumn}`}
        onClick={onSelect}
      >
        <div className={styles.pipColumnHeader}>
          <span className={`${styles.systemIcon} ${styles.powerIcon}`}>
            {component.icon}
          </span>
          <span className={styles.systemName}>{component.name}</span>
        </div>
        
        <div className={styles.pipColumnSpacer} />
        
        <div className={styles.pipStackMultiRow}>
          {columns.map((colPips, colIdx) => (
            <div key={colIdx} className={styles.pipStackColumn}>
              {colPips.reverse()}
            </div>
          ))}
        </div>
        
        <div className={styles.powerLabel}>
          {availablePips} FREE
        </div>
        
        {selectorButton}
      </div>
    );
  }
  
  // Standard single-column pip stack
  const pips: React.ReactNode[] = [];
  const hasMinimumRequirement = component.minPips > 0 && !isPowerColumn;
  const optionalPips = component.maxPips - component.minPips; // Pips above minimum
  
  // Generate OPTIONAL pips (above minimum) - from top to bottom
  for (let i = component.maxPips - 1; i >= component.minPips; i--) {
    const isFilled = component.isOn && i < component.allocatedPips;
    const pipNumber = i + 1;
    
    // Can we click this pip? Only if we have available pips OR we're reducing
    const canIncrease = !isPowerColumn && availablePips > 0;
    const wouldIncrease = pipNumber > component.allocatedPips;
    const canClick = !readOnly && !disabled && component.isOn && (!wouldIncrease || canIncrease);
    
    pips.push(
      <div
        key={i}
        className={`
          ${styles.pip} 
          ${isFilled ? styles.pipFilled : styles.pipEmpty} 
          ${disabled ? styles.pipDisabled : ''}
          ${isPowerColumn ? styles.powerPip : ''}
          ${isPowerColumn && !isFilled ? styles.pipAvailable : ''}
        `}
        onClick={(e) => {
          e.stopPropagation();
          if (canClick) onPipChange(pipNumber);
        }}
        style={{ cursor: canClick ? 'pointer' : 'default' }}
        title={isPowerColumn 
          ? `${isFilled ? 'Allocated' : 'Available'} pip` 
          : `${pipNumber} pip${pipNumber !== 1 ? 's' : ''}${!canClick && wouldIncrease ? ' (no available power)' : ''}`
        }
      />
    );
  }
  
  // Add threshold marker between optional and required pips
  if (hasMinimumRequirement && optionalPips > 0) {
    pips.push(
      <div key="threshold" className={styles.minThreshold} title="Minimum power required">
        <span className={styles.minThresholdLabel}>MIN</span>
      </div>
    );
  }
  
  // Add FUSED minimum required pips as a single block
  if (hasMinimumRequirement && component.minPips > 0) {
    const minPipsFilled = component.isOn ? Math.min(component.allocatedPips, component.minPips) : 0;
    const isFilled = minPipsFilled === component.minPips;
    
    // Calculate height: each pip is 14px + 4px gap, fused block combines them
    // Height = (numPips * 14px) + ((numPips - 1) * 4px) for seamless appearance
    const fusedHeight = (component.minPips * 14) + ((component.minPips - 1) * 4);
    
    // Create divider lines to show pip count within fused block
    const dividers: React.ReactNode[] = [];
    for (let d = 1; d < component.minPips; d++) {
      // Position each divider at the boundary between pips
      // Each pip is 14px + 4px gap, so divider at (d * 18) - 2 from bottom
      const bottomOffset = (d * 18) - 2;
      dividers.push(
        <div 
          key={`divider-${d}`} 
          className={styles.pipFusedDivider}
          style={{ bottom: `${bottomOffset}px` }}
        />
      );
    }
    
    pips.push(
      <div
        key="fused-min"
        className={`
          ${styles.pip}
          ${styles.pipFused}
          ${isFilled ? styles.pipRequiredFilled : styles.pipRequiredEmpty}
          ${disabled ? styles.pipDisabled : ''}
        `}
        style={{ 
          height: `${fusedHeight}px`,
          cursor: 'default'
        }}
        title={`${component.minPips} pip${component.minPips !== 1 ? 's' : ''} (REQUIRED - cannot be reduced)`}
      >
        {dividers}
      </div>
    );
  } else if (isPowerColumn) {
    // Power column renders all pips normally in the optional loop above
    // Nothing to do here since power column has no minPips
  }

  // Is this a utility mode component (mining, tractor, salvage)?
  const isUtilityMode = component.utilityMode;

  return (
    <div 
      className={`${styles.pipColumn} ${isSelected ? styles.pipColumnSelected : ''} ${disabled ? styles.pipColumnDisabled : ''} ${isPowerColumn ? styles.powerColumn : ''} ${isUtilityMode ? styles.pipColumnUtility : ''}`}
      onClick={onSelect}
    >
      <div className={styles.pipColumnHeader}>
        <span className={`${styles.systemIcon} ${component.isOn && !disabled ? styles.systemIconOn : ''} ${isPowerColumn ? styles.powerIcon : ''}`}>
          {component.icon}
        </span>
        <span className={styles.systemName}>{component.name}</span>
      </div>
      
      <div className={styles.pipColumnSpacer} />
      
      <div className={styles.pipStack}>
        {pips}
      </div>
      
      {!isPowerColumn && (
        <button 
          className={`${styles.toggleButton} ${component.isOn ? styles.toggleOn : styles.toggleOff}`}
          onClick={(e) => {
            e.stopPropagation();
            if (!readOnly && !disabled) onToggle();
          }}
          disabled={readOnly || disabled}
        >
          {component.isOn ? 'ON' : 'OFF'}
        </button>
      )}
      
      {isPowerColumn && (
        <div className={styles.powerLabel}>
          {availablePips} FREE
        </div>
      )}
      
      {selectorButton}
    </div>
  );
};

/**
 * Main Power Management Panel
 */
export const PowerManagementPanel: React.FC<PowerManagementPanelProps> = ({
  ship,
  hardpoints,
  readOnly = false,
  onComponentChange,
  hardpointOverrides,
  onLoadedItemChange,
  loadedItemOverrides,
  onResetAll,
  onPowerAllocationsChange,
  weaponContent
}) => {
  const [mode, setMode] = useState<'scm' | 'nav'>('scm');
  const [selectedComponent, setSelectedComponent] = useState<string>('power');
  
  // Power plant swap UI state
  const [showPowerPlantDropdown, setShowPowerPlantDropdown] = useState(false);
  const [availablePowerPlants, setAvailablePowerPlants] = useState<ComponentItem[]>([]);
  const [loadingPowerPlants, setLoadingPowerPlants] = useState(false);
  const [selectedPowerPlantHardpoint, setSelectedPowerPlantHardpoint] = useState<number | null>(null);

  // Shield swap UI state
  const [showShieldDropdown, setShowShieldDropdown] = useState(false);
  const [availableShields, setAvailableShields] = useState<ComponentItem[]>([]);
  const [loadingShields, setLoadingShields] = useState(false);
  const [selectedShieldHardpoint, setSelectedShieldHardpoint] = useState<number | null>(null);

  // Cooler swap UI state
  const [showCoolerDropdown, setShowCoolerDropdown] = useState(false);
  const [availableCoolers, setAvailableCoolers] = useState<ComponentItem[]>([]);
  const [loadingCoolers, setLoadingCoolers] = useState(false);
  const [selectedCoolerHardpoint, setSelectedCoolerHardpoint] = useState<number | null>(null);

  // Quantum drive swap UI state
  const [showQuantumDropdown, setShowQuantumDropdown] = useState(false);
  const [availableQuantumDrives, setAvailableQuantumDrives] = useState<ComponentItem[]>([]);
  const [loadingQuantumDrives, setLoadingQuantumDrives] = useState(false);

  // Radar swap UI state
  const [showRadarDropdown, setShowRadarDropdown] = useState(false);
  const [availableRadars, setAvailableRadars] = useState<ComponentItem[]>([]);
  const [loadingRadars, setLoadingRadars] = useState(false);

  // Mining equipment swap UI state
  const [showMiningDropdown, setShowMiningDropdown] = useState(false);
  const [availableMiningLasers, setAvailableMiningLasers] = useState<ComponentItem[]>([]);
  const [loadingMiningLasers, setLoadingMiningLasers] = useState(false);
  const [selectedMiningHardpoint, setSelectedMiningHardpoint] = useState<number | null>(null);

  // Mining module (gadget) swap UI state
  const [showMiningModuleDropdown, setShowMiningModuleDropdown] = useState(false);
  const [availableMiningModules, setAvailableMiningModules] = useState<ComponentItem[]>([]);
  const [loadingMiningModules, setLoadingMiningModules] = useState(false);
  const [selectedMiningModuleHardpoint, setSelectedMiningModuleHardpoint] = useState<number | null>(null);
  // Track added modules per hardpoint (hardpointId -> array of ComponentItems)
  const [addedMiningModules, setAddedMiningModules] = useState<Map<number, ComponentItem[]>>(new Map());

  // Tractor beam swap UI state
  const [showTractorDropdown, setShowTractorDropdown] = useState(false);
  const [availableTractorBeams, setAvailableTractorBeams] = useState<ComponentItem[]>([]);
  const [loadingTractorBeams, setLoadingTractorBeams] = useState(false);
  const [selectedTractorHardpoint, setSelectedTractorHardpoint] = useState<number | null>(null);

  // Salvage equipment swap UI state
  const [showSalvageDropdown, setShowSalvageDropdown] = useState(false);
  const [availableSalvageHeads, setAvailableSalvageHeads] = useState<ComponentItem[]>([]);
  const [loadingSalvageHeads, setLoadingSalvageHeads] = useState(false);
  const [selectedSalvageHardpoint, setSelectedSalvageHardpoint] = useState<number | null>(null);
  const [selectedSalvageLoadedItemId, setSelectedSalvageLoadedItemId] = useState<number | null>(null);

  // Salvage module (gadget) swap UI state
  const [showSalvageModuleDropdown, setShowSalvageModuleDropdown] = useState(false);
  const [availableSalvageModules, setAvailableSalvageModules] = useState<ComponentItem[]>([]);
  const [loadingSalvageModules, setLoadingSalvageModules] = useState(false);
  const [selectedSalvageModuleHardpoint, setSelectedSalvageModuleHardpoint] = useState<number | null>(null);
  const [selectedSalvageModuleForSwap, setSelectedSalvageModuleForSwap] = useState<number | null>(null);
  // Track added salvage modules per hardpoint (hardpointId -> array of ComponentItems)
  const [addedSalvageModules, setAddedSalvageModules] = useState<Map<number, ComponentItem[]>>(new Map());

  // Mining equipment swap UI state - for swapping mining lasers (loaded items)
  const [selectedMiningLoadedItemId, setSelectedMiningLoadedItemId] = useState<number | null>(null);

  // Helper function to parse manufacturer and class from description
  // Description format: "Item Type: Power Plant\nManufacturer: ACOM\nSize: 1\nGrade: A\nClass: Competition\n\nDescription..."
  // Note: Database stores literal \n as text (backslash + n), not as newline characters
  const parseComponentDescription = useCallback((description: string | undefined): { manufacturer: string; classification: string } => {
    if (!description) return { manufacturer: '', classification: '' };
    
    let manufacturer = '';
    let classification = '';
    
    // Match "Manufacturer: X" followed by literal \n or end of string
    // The \\\\n matches the literal text "\n" (backslash + n)
    const mfgMatch = description.match(/Manufacturer:\s*(.+?)(?:\\n|$)/i);
    if (mfgMatch) {
      manufacturer = mfgMatch[1].trim();
    }
    
    // Match "Class: X" followed by literal \n or end of string  
    const classMatch = description.match(/Class:\s*(.+?)(?:\\n|$)/i);
    if (classMatch) {
      classification = classMatch[1].trim();
    }
    
    return { manufacturer, classification };
  }, []);

  // Extract power plant info from hardpoints (with overrides applied)
  const powerPlantInfo = useMemo((): PowerPlantInfo[] => {
    const powerPlantHardpoints = hardpoints.filter(h => 
      h.hardpointType === 'PowerPlant' ||
      h.itemDefinition?.type?.toLowerCase().includes('powerplant')
    );

    return powerPlantHardpoints.map(hp => {
      // Check if this hardpoint has an override
      const override = hardpointOverrides?.get(hp.id);
      
      if (override) {
        const parsed = parseComponentDescription(override.description);
        return {
          hardpointId: hp.id,
          slotSize: hp.size || 1,
          size: override.size || 1,
          itemName: override.itemName,
          displayName: override.localizedName || override.itemName,
          manufacturer: parsed.manufacturer || override.manufacturer || 'Unknown',
          grade: override.grade || 3,
          subType: override.subType || 'PowerPlant',
          classification: parsed.classification,
          powerUnits: override.powerPipStats?.powerUnits || 14,
          powerOutput: override.powerPlantStats?.powerOutput || null,
          description: override.description || '',
          health: override.health || 0
        };
      }
      
      const parsed = parseComponentDescription(hp.itemDefinition?.description);
      return {
        hardpointId: hp.id,
        slotSize: hp.size || 1,
        size: hp.itemDefinition?.size || hp.size || 1,
        itemName: hp.itemDefinition?.itemName || hp.defaultItemName || 'Unknown',
        displayName: hp.itemDefinition?.localizedName || hp.defaultItemName || 'Unknown Power Plant',
        manufacturer: parsed.manufacturer || hp.itemDefinition?.manufacturer || 'Unknown',
        grade: hp.itemDefinition?.grade || 3,
        subType: hp.itemDefinition?.subType || 'PowerPlant',
        classification: parsed.classification,
        powerUnits: hp.itemDefinition?.powerPipStats?.powerUnits || 14,
        powerOutput: hp.itemDefinition?.powerPlantStats?.powerOutput || null,
        description: hp.itemDefinition?.description || '',
        health: hp.itemDefinition?.health || 0
      };
    });
  }, [hardpoints, hardpointOverrides, parseComponentDescription]);

  // Extract shield info from hardpoints (with overrides applied)
  // Filter out ShieldController - we only want actual shield generators
  const shieldInfo = useMemo((): ShieldInfo[] => {
    const shieldHardpoints = hardpoints.filter(h => {
      const itemType = h.itemDefinition?.type?.toLowerCase() || '';
      // Exclude ShieldController - it's not a swappable shield generator
      if (itemType === 'shieldcontroller') return false;
      // Must be a shield hardpoint with an actual shield item
      return (h.hardpointType === 'Shield' || itemType === 'shield' || itemType === 'shieldgenerator') &&
             itemType !== 'shieldcontroller';
    });

    return shieldHardpoints.map(hp => {
      // Check if this hardpoint has an override
      const override = hardpointOverrides?.get(hp.id);
      
      if (override) {
        const parsed = parseComponentDescription(override.description);
        return {
          hardpointId: hp.id,
          slotSize: hp.size || 1,
          size: override.size || 1,
          itemName: override.itemName,
          displayName: override.localizedName || override.itemName,
          manufacturer: parsed.manufacturer || override.manufacturer || 'Unknown',
          grade: override.grade || 3,
          subType: override.subType || 'Shield',
          classification: parsed.classification,
          powerUnits: override.powerPipStats?.powerUnits || 2,
          shieldHealthMax: override.shieldStats?.shieldHealthMax || null,
          regenRate: override.shieldStats?.regenRate || null,
          description: override.description || '',
          health: override.health || 0
        };
      }
      
      const parsed = parseComponentDescription(hp.itemDefinition?.description);
      return {
        hardpointId: hp.id,
        slotSize: hp.size || 1,
        size: hp.itemDefinition?.size || hp.size || 1,
        itemName: hp.itemDefinition?.itemName || hp.defaultItemName || 'Unknown',
        displayName: hp.itemDefinition?.localizedName || hp.defaultItemName || 'Unknown Shield',
        manufacturer: parsed.manufacturer || hp.itemDefinition?.manufacturer || 'Unknown',
        grade: hp.itemDefinition?.grade || 3,
        subType: hp.itemDefinition?.subType || 'Shield',
        classification: parsed.classification,
        powerUnits: hp.itemDefinition?.powerPipStats?.powerUnits || 2,
        shieldHealthMax: hp.itemDefinition?.shieldStats?.shieldHealthMax || null,
        regenRate: hp.itemDefinition?.shieldStats?.regenRate || null,
        description: hp.itemDefinition?.description || '',
        health: hp.itemDefinition?.health || 0
      };
    });
  }, [hardpoints, hardpointOverrides, parseComponentDescription]);

  // Extract cooler info from hardpoints (with overrides applied)
  const coolerInfo = useMemo((): CoolerInfo[] => {
    const coolerHardpoints = hardpoints.filter(h => {
      // Must be a cooler hardpoint (not a controller)
      if (h.hardpointType !== 'Cooler') return false;
      // Exclude controller items
      if (h.defaultItemName?.toLowerCase().startsWith('controller_')) return false;
      if (h.slotName?.toLowerCase().includes('controller')) return false;
      return true;
    });

    return coolerHardpoints.map(hp => {
      // Check if this hardpoint has an override
      const override = hardpointOverrides?.get(hp.id);
      
      if (override) {
        const parsed = parseComponentDescription(override.description);
        return {
          hardpointId: hp.id,
          slotSize: hp.size || 1,
          size: override.size || 1,
          itemName: override.itemName,
          displayName: override.localizedName || override.itemName,
          manufacturer: parsed.manufacturer || override.manufacturer || 'Unknown',
          grade: override.grade || 3,
          subType: override.subType || 'Cooler',
          classification: parsed.classification,
          powerUnits: override.powerPipStats?.powerUnits || 3,
          coolingRate: override.coolerStats?.coolingRate || null,
          description: override.description || '',
          health: override.health || 0
        };
      }
      
      const parsed = parseComponentDescription(hp.itemDefinition?.description);
      return {
        hardpointId: hp.id,
        slotSize: hp.size || 1,
        size: hp.itemDefinition?.size || hp.size || 1,
        itemName: hp.itemDefinition?.itemName || hp.defaultItemName || 'Unknown',
        displayName: hp.itemDefinition?.localizedName || hp.defaultItemName || 'Unknown Cooler',
        manufacturer: parsed.manufacturer || hp.itemDefinition?.manufacturer || 'Unknown',
        grade: hp.itemDefinition?.grade || 3,
        subType: hp.itemDefinition?.subType || 'Cooler',
        classification: parsed.classification,
        powerUnits: hp.itemDefinition?.powerPipStats?.powerUnits || 3,
        coolingRate: hp.itemDefinition?.coolerStats?.coolingRate || null,
        description: hp.itemDefinition?.description || '',
        health: hp.itemDefinition?.health || 0
      };
    });
  }, [hardpoints, hardpointOverrides, parseComponentDescription]);

  // Extract quantum drive info from hardpoints (with overrides applied)
  const quantumDriveInfo = useMemo((): QuantumDriveInfo | null => {
    const qdHardpoint = hardpoints.find(h => 
      h.hardpointType === 'QuantumDrive' ||
      h.itemDefinition?.type?.toLowerCase() === 'quantumdrive'
    );

    if (!qdHardpoint) return null;

    // Check if this hardpoint has an override
    const override = hardpointOverrides?.get(qdHardpoint.id);
    
    if (override) {
      const parsed = parseComponentDescription(override.description);
      return {
        hardpointId: qdHardpoint.id,
        slotSize: qdHardpoint.size || 1,
        size: override.size || 1,
        itemName: override.itemName,
        displayName: override.localizedName || override.itemName,
        manufacturer: parsed.manufacturer || override.manufacturer || 'Unknown',
        grade: override.grade || 3,
        subType: override.subType || 'QuantumDrive',
        classification: parsed.classification,
        driveSpeed: override.quantumDriveStats?.driveSpeed || null,
        spoolUpTime: override.quantumDriveStats?.spoolUpTime || null,
        cooldownTime: override.quantumDriveStats?.cooldownTime || null,
        fuelRequirement: override.quantumDriveStats?.fuelRequirement || null,
        description: override.description || '',
        health: override.health || 0
      };
    }
    
    const parsed = parseComponentDescription(qdHardpoint.itemDefinition?.description);
    return {
      hardpointId: qdHardpoint.id,
      slotSize: qdHardpoint.size || 1,
      size: qdHardpoint.itemDefinition?.size || qdHardpoint.size || 1,
      itemName: qdHardpoint.itemDefinition?.itemName || qdHardpoint.defaultItemName || 'Unknown',
      displayName: qdHardpoint.itemDefinition?.localizedName || qdHardpoint.defaultItemName || 'Unknown QD',
      manufacturer: parsed.manufacturer || qdHardpoint.itemDefinition?.manufacturer || 'Unknown',
      grade: qdHardpoint.itemDefinition?.grade || 3,
      subType: qdHardpoint.itemDefinition?.subType || 'QuantumDrive',
      classification: parsed.classification,
      driveSpeed: qdHardpoint.itemDefinition?.quantumDriveStats?.driveSpeed || null,
      spoolUpTime: qdHardpoint.itemDefinition?.quantumDriveStats?.spoolUpTime || null,
      cooldownTime: qdHardpoint.itemDefinition?.quantumDriveStats?.cooldownTime || null,
      fuelRequirement: qdHardpoint.itemDefinition?.quantumDriveStats?.fuelRequirement || null,
      description: qdHardpoint.itemDefinition?.description || '',
      health: qdHardpoint.itemDefinition?.health || 0
    };
  }, [hardpoints, hardpointOverrides, parseComponentDescription]);

  // Extract radar info from hardpoints (with overrides applied)
  const radarInfo = useMemo((): RadarInfo | null => {
    const radarHardpoint = hardpoints.find(h => 
      h.hardpointType === 'Radar' ||
      h.itemDefinition?.type?.toLowerCase() === 'radar'
    );

    if (!radarHardpoint) return null;

    // Check if this hardpoint has an override
    const override = hardpointOverrides?.get(radarHardpoint.id);
    
    if (override) {
      const parsed = parseComponentDescription(override.description);
      return {
        hardpointId: radarHardpoint.id,
        slotSize: radarHardpoint.size || 1,
        size: override.size || 1,
        itemName: override.itemName,
        displayName: override.localizedName || override.itemName,
        manufacturer: parsed.manufacturer || override.manufacturer || 'Unknown',
        grade: override.grade || 3,
        subType: override.subType || 'Radar',
        classification: parsed.classification,
        powerUnits: override.powerPipStats?.powerUnits || 3,
        description: override.description || '',
        health: override.health || 0,
        sensitivity: override.radarStats?.sensitivity || 0,
        piercing: override.radarStats?.piercing || 0
      };
    }
    
    const parsed = parseComponentDescription(radarHardpoint.itemDefinition?.description);
    return {
      hardpointId: radarHardpoint.id,
      slotSize: radarHardpoint.size || 1,
      size: radarHardpoint.itemDefinition?.size || radarHardpoint.size || 1,
      itemName: radarHardpoint.itemDefinition?.itemName || radarHardpoint.defaultItemName || 'Unknown',
      displayName: radarHardpoint.itemDefinition?.localizedName || radarHardpoint.defaultItemName || 'Unknown Radar',
      manufacturer: parsed.manufacturer || radarHardpoint.itemDefinition?.manufacturer || 'Unknown',
      grade: radarHardpoint.itemDefinition?.grade || 3,
      subType: radarHardpoint.itemDefinition?.subType || 'Radar',
      classification: parsed.classification,
      powerUnits: radarHardpoint.itemDefinition?.powerPipStats?.powerUnits || 3,
      description: radarHardpoint.itemDefinition?.description || '',
      health: radarHardpoint.itemDefinition?.health || 0,
      sensitivity: radarHardpoint.itemDefinition?.radarStats?.sensitivity || 0,
      piercing: radarHardpoint.itemDefinition?.radarStats?.piercing || 0
    };
  }, [hardpoints, hardpointOverrides, parseComponentDescription]);

  // Extract mining equipment info from hardpoints (with overrides applied)
  const miningInfo = useMemo((): UtilityEquipmentInfo[] => {
    const equipment: UtilityEquipmentInfo[] = [];
    
    hardpoints.forEach(hp => {
      const slotName = (hp.slotName || '').toLowerCase();
      const itemType = (hp.itemDefinition?.type || '').toLowerCase();
      const defaultName = (hp.defaultItemName || '').toLowerCase();
      
      // Skip controllers
      if (slotName.includes('controller') || defaultName.includes('controller')) return;
      
      // Skip ore pods - they are storage containers, not mining equipment (no pips)
      const isOrePod = slotName.includes('pod') || defaultName.includes('pod') || itemType === 'container';
      if (isOrePod) return;
      
      // Skip salvage slots - they're not mining!
      if (slotName.includes('salvage')) return;
      if (slotName.includes('salvage')) return;
      
      // Check for mining equipment - must be mining-related slot OR have WeaponMining item
      const isMiningItem = itemType === 'weaponmining';
      const isMiningSlot = slotName.includes('mining');
      const isUtilityTurret = itemType === 'utilityturret' && slotName.includes('mining');
      const isToolArmMining = itemType === 'toolarm' && slotName.includes('mining');
      
      if (isMiningItem || isMiningSlot || isUtilityTurret || isToolArmMining) {
        const override = hardpointOverrides?.get(hp.id);
        let miningItem = override || hp.itemDefinition;
        let miningLaserInfo: UtilityEquipmentInfo['miningLaser'] = undefined;
        let modulesInfo: UtilityEquipmentInfo['modules'] = [];
        
        // For ToolArm/turrets, look for actual mining laser in loadedItems
        if (hp.loadedItems && hp.loadedItems.length > 0) {
          const findMiningLaser = (items: any[]): any | null => {
            for (const item of items) {
              const type = (item.itemDefinition?.type || '').toLowerCase();
              if (type === 'weaponmining') return item;
              if (item.childAttachments?.length > 0) {
                const found = findMiningLaser(item.childAttachments);
                if (found) return found;
              }
            }
            return null;
          };
          const foundItem = findMiningLaser(hp.loadedItems as any[]);
          if (foundItem) {
            // Check for override on this loaded item
            const overrideKey = `${hp.id}:${foundItem.id}`;
            const loadedOverride = loadedItemOverrides?.get(overrideKey);
            const laserDef = loadedOverride || foundItem.itemDefinition;
            
            miningItem = laserDef;
            // Use actual ModuleSlots from database (parsed from P4K XML ItemPort definitions)
            const laserSize = laserDef?.size || foundItem.size || 1;
            const actualModuleSlots = laserDef?.moduleSlots || 3; // Default to 3 if not specified
            
            miningLaserInfo = {
              loadedItemId: foundItem.id,  // The LoadedItem record ID for swapping
              itemId: laserDef?.id || 0,
              itemName: laserDef?.itemName || foundItem.itemName || '',
              displayName: laserDef?.localizedName || 'Mining Laser',
              size: laserSize,
              manufacturer: laserDef?.manufacturer || 'Unknown',
              moduleSlots: actualModuleSlots,
              // Add mining laser stats
              instability: laserDef?.miningLaserStats?.instability,
              resistance: laserDef?.miningLaserStats?.resistance,
              optimalWindow: laserDef?.miningLaserStats?.optimalWindow,
              power: laserDef?.miningLaserStats?.power,
              range: laserDef?.miningLaserStats?.range
            };
            
            // Look for modules (MiningModifier) in childAttachments of the mining laser
            if (foundItem.childAttachments && foundItem.childAttachments.length > 0) {
              foundItem.childAttachments.forEach((child: any) => {
                const childDef = child.itemDefinition;
                const childType = (childDef?.type || '').toLowerCase();
                if (childType === 'miningmodifier' || childType === 'salvagemodifier') {
                  let modDisplayName = childDef?.localizedName || '';
                  if (!modDisplayName || modDisplayName === 'Unknown Item') {
                    modDisplayName = childType === 'miningmodifier' ? 'Mining Module' : 'Module';
                  }
                  modulesInfo.push({
                    loadedItemId: child.id,  // The ChildAttachment record ID for swapping
                    itemId: childDef?.id || 0,
                    itemName: childDef?.itemName || child.itemName || '',
                    displayName: modDisplayName,
                    size: childDef?.size || 1,
                    manufacturer: childDef?.manufacturer || 'Unknown',
                    type: childDef?.type || 'Unknown',
                    description: childDef?.description || '',
                    tags: childDef?.tags || '',
                    // Store the full definition for access to all properties
                    fullDef: childDef
                  });
                }
              });
            }
          }
        }
        
        // Only add if we found an actual mining laser (WeaponMining type) or it's a mining slot
        if (miningItem && (miningItem.type?.toLowerCase() === 'weaponmining' || isMiningSlot)) {
          // Determine display name - for turrets/arms show the mount name, for direct lasers show laser name
          let displayName = miningItem?.localizedName || miningItem?.itemName || 'Mining Laser';
          if (isUtilityTurret || isToolArmMining) {
            // For turrets/arms, show the turret/arm name, not the laser
            displayName = hp.itemDefinition?.localizedName || hp.defaultItemDisplayName || 'Mining Turret';
            if (displayName === 'Unknown Item') {
              if (slotName.includes('left')) displayName = 'Mining Arm (Left)';
              else if (slotName.includes('right')) displayName = 'Mining Arm (Right)';
              else displayName = isUtilityTurret ? 'Mining Turret' : 'Mining Arm';
            }
          }
          
          equipment.push({
            hardpointId: hp.id,
            slotSize: hp.size || miningItem?.size || 1,
            itemName: miningItem?.itemName || hp.defaultItemName || '',
            displayName,
            manufacturer: miningItem?.manufacturer || 'Unknown',
            grade: miningItem?.grade || 3,
            powerUnits: miningItem?.powerPipStats?.powerUnits || 1,
            health: miningItem?.health || 0,
            type: 'mining',
            miningLaser: miningLaserInfo,
            modules: modulesInfo.length > 0 ? modulesInfo : undefined,
            // Add mining stats directly if this is a direct mining laser (not nested)
            miningStats: miningLaserInfo ? undefined : {
              instability: miningItem?.miningLaserStats?.instability,
              resistance: miningItem?.miningLaserStats?.resistance,
              optimalWindow: miningItem?.miningLaserStats?.optimalWindow,
              power: miningItem?.miningLaserStats?.power,
              range: miningItem?.miningLaserStats?.range
            }
          });
        }
      }
    });
    
    return equipment;
  }, [hardpoints, hardpointOverrides, loadedItemOverrides]);

  // Extract tractor beam info from hardpoints (with overrides applied)
  const tractorInfo = useMemo((): UtilityEquipmentInfo[] => {
    const equipment: UtilityEquipmentInfo[] = [];
    
    hardpoints.forEach(hp => {
      const slotName = (hp.slotName || '').toLowerCase();
      const itemType = (hp.itemDefinition?.type || '').toLowerCase();
      const defaultName = (hp.defaultItemName || '').toLowerCase();
      
      // Skip controllers
      if (slotName.includes('controller') || defaultName.includes('controller')) return;
      
      // Check for tractor beam
      if (itemType === 'tractorbeam' || slotName.includes('tractor') || defaultName.includes('tractor')) {
        const override = hardpointOverrides?.get(hp.id);
        const tractorItem = override || hp.itemDefinition;
        
        if (tractorItem && (tractorItem.type?.toLowerCase() === 'tractorbeam' || itemType === 'tractorbeam')) {
          equipment.push({
            hardpointId: hp.id,
            slotSize: hp.size || tractorItem?.size || 1,
            itemName: tractorItem?.itemName || hp.defaultItemName || '',
            displayName: tractorItem?.localizedName || tractorItem?.itemName || 'Tractor Beam',
            manufacturer: tractorItem?.manufacturer || 'Unknown',
            grade: tractorItem?.grade || 3,
            powerUnits: tractorItem?.powerPipStats?.powerUnits || 1,
            health: tractorItem?.health || 0,
            type: 'tractor',
            // Add tractor beam stats
            tractorStats: {
              range: tractorItem?.tractorBeamStats?.range,
              force: tractorItem?.tractorBeamStats?.force,
              volume: tractorItem?.tractorBeamStats?.volume,
              cargoRange: tractorItem?.tractorBeamStats?.cargoRange,
              cargoForce: tractorItem?.tractorBeamStats?.cargoForce
            }
          });
        }
      }
    });
    
    return equipment;
  }, [hardpoints, hardpointOverrides]);

  // Extract salvage equipment info from hardpoints (with overrides applied)
  const salvageInfo = useMemo((): UtilityEquipmentInfo[] => {
    const equipment: UtilityEquipmentInfo[] = [];
    
    hardpoints.forEach(hp => {
      const slotName = (hp.slotName || '').toLowerCase();
      const itemType = (hp.itemDefinition?.type || '').toLowerCase();
      const defaultName = (hp.defaultItemName || '').toLowerCase();
      
      // Skip controllers
      if (slotName.includes('controller') || defaultName.includes('controller')) return;
      
      // Skip internal salvage components - they don't consume pips
      const isInternalSalvage = defaultName.includes('filler') || defaultName.includes('internalstorage') ||
                                defaultName.includes('disintegration') || defaultName.includes('fieldsupporter') ||
                                defaultName.includes('buff_modifier') || itemType === 'container';
      if (isInternalSalvage) return;
      
      // Skip mining slots - they're not salvage!
      if (slotName.includes('mining')) return;
      
      // Check for salvage equipment - must be salvage-related slot OR have SalvageHead item
      const isSalvageSlot = slotName.includes('salvage') && slotName.includes('arm');
      const isSalvageItem = itemType === 'salvagehead';
      const isToolArmSalvage = itemType === 'toolarm' && slotName.includes('salvage');
      
      if (isSalvageSlot || isSalvageItem || isToolArmSalvage) {
        const override = hardpointOverrides?.get(hp.id);
        let salvageItem = override || hp.itemDefinition;
        
        // For ToolArm, look for actual salvage head in loadedItems
        let salvageHeadInfo: UtilityEquipmentInfo['salvageHead'] = undefined;
        let modulesInfo: NonNullable<UtilityEquipmentInfo['modules']> = [];
        
        if (hp.loadedItems && hp.loadedItems.length > 0) {
          const findSalvageHead = (items: any[]): any | null => {
            for (const item of items) {
              const type = (item.itemDefinition?.type || '').toLowerCase();
              if (type === 'salvagehead') return item;
              if (item.childAttachments?.length > 0) {
                const found = findSalvageHead(item.childAttachments);
                if (found) return found;
              }
            }
            return null;
          };
          const foundItem = findSalvageHead(hp.loadedItems as any[]);
          
          if (foundItem) {
            // Check for override on this loaded item
            const overrideKey = `${hp.id}:${foundItem.id}`;
            const loadedOverride = loadedItemOverrides?.get(overrideKey);
            const headDef = loadedOverride || foundItem.itemDefinition;
            
            let headDisplayName = headDef?.localizedName || '';
            if (!headDisplayName || headDisplayName === 'Unknown Item') {
              headDisplayName = 'Salvage Head';
            }
            
            // Use actual ModuleSlots from database (parsed from P4K XML ItemPort definitions)
            const headSize = headDef?.size || foundItem.size || 1;
            const actualModuleSlots = headDef?.moduleSlots || 3; // Default to 3 if not specified
            
            salvageHeadInfo = {
              loadedItemId: foundItem.id,  // The LoadedItem record ID for swapping
              itemId: headDef?.id || 0,
              itemName: headDef?.itemName || foundItem.itemName || '',
              displayName: headDisplayName,
              size: headSize,
              manufacturer: headDef?.manufacturer || 'Unknown',
              moduleSlots: actualModuleSlots
            };
            
            // Look for modules (SalvageModifier) in childAttachments of the salvage head
            if (foundItem.childAttachments && foundItem.childAttachments.length > 0) {
              foundItem.childAttachments.forEach((child: any) => {
                // Check for override on this child attachment (module)
                const childOverrideKey = `${hp.id}:${child.id}`;
                const childOverride = loadedItemOverrides?.get(childOverrideKey);
                const childDef = childOverride || child.itemDefinition;
                
                const childType = (childDef?.type || '').toLowerCase();
                const itemNameToCheck = childDef?.itemName || child.itemName || '';
                if (childType === 'salvagemodifier' || childType === 'miningmodifier') {
                  // Check for duplicates before adding
                  const exists = modulesInfo.some(m => m.itemName === itemNameToCheck);
                  if (!exists) {
                    let modDisplayName = childDef?.localizedName || '';
                    if (!modDisplayName || modDisplayName === 'Unknown Item') {
                      modDisplayName = childType === 'salvagemodifier' ? 'Salvage Module' : 'Module';
                    }
                    modulesInfo.push({
                      loadedItemId: child.id,  // The ChildAttachment record ID for swapping
                      itemId: childDef?.id || 0,
                      itemName: itemNameToCheck,
                      displayName: modDisplayName,
                      size: childDef?.size || 1,
                      manufacturer: childDef?.manufacturer || 'Unknown',
                      type: childDef?.type || 'Unknown',
                      description: childDef?.description || ''
                    });
                  }
                }
              });
            }
          }
          
          // Also check for modules/attachments directly in loadedItems (not nested)
          // Some ships have modules directly attached to the arm, not to a salvage head
          (hp.loadedItems as any[]).forEach((item: any) => {
            // Check for override on this loaded item
            const itemOverrideKey = `${hp.id}:${item.id}`;
            const itemOverride = loadedItemOverrides?.get(itemOverrideKey);
            const itemDef = itemOverride || item.itemDefinition;
            
            const itemType = (itemDef?.type || '').toLowerCase();
            const itemName = (item.itemName || '').toLowerCase();
            const itemNameToCheck = itemDef?.itemName || item.itemName || '';
            
            // Check if this is a salvage modifier or similar module
            if (itemType === 'salvagemodifier' || itemName.includes('salvage_mod') || itemName.includes('abrade') || itemName.includes('scraper')) {
              // Check for duplicates before adding
              const exists = modulesInfo.some(m => m.itemName === itemNameToCheck);
              if (!exists) {
                let modDisplayName = itemDef?.localizedName || '';
                if (!modDisplayName || modDisplayName === 'Unknown Item') {
                  modDisplayName = 'Salvage Module';
                }
                modulesInfo.push({
                  loadedItemId: item.id,  // The LoadedItem record ID for swapping
                  itemId: itemDef?.id || 0,
                  itemName: itemNameToCheck,
                  displayName: modDisplayName,
                  size: itemDef?.size || 1,
                  manufacturer: itemDef?.manufacturer || 'Unknown',
                  type: itemDef?.type || 'Unknown',
                  description: itemDef?.description || ''
                });
              }
            }
            
            // Also check childAttachments of each loaded item
            if (item.childAttachments && item.childAttachments.length > 0) {
              item.childAttachments.forEach((child: any) => {
                // Check for override on this child attachment
                const childOverrideKey = `${hp.id}:${child.id}`;
                const childOverride = loadedItemOverrides?.get(childOverrideKey);
                const childDef = childOverride || child.itemDefinition;
                
                const childType = (childDef?.type || '').toLowerCase();
                const childName = (child.itemName || '').toLowerCase();
                const itemNameToCheck = childDef?.itemName || child.itemName || '';
                
                if (childType === 'salvagemodifier' || childName.includes('salvage_mod') || childName.includes('abrade') || childName.includes('scraper')) {
                  // Avoid duplicates - check across all previously added modules
                  const exists = modulesInfo.some(m => m.itemName === itemNameToCheck);
                  if (!exists) {
                    let modDisplayName = childDef?.localizedName || '';
                    if (!modDisplayName || modDisplayName === 'Unknown Item') {
                      modDisplayName = 'Salvage Module';
                    }
                    modulesInfo.push({
                      loadedItemId: child.id || 0,  // The ChildAttachment record ID
                      itemId: childDef?.id || 0,
                      itemName: itemNameToCheck,
                      displayName: modDisplayName,
                      size: childDef?.size || 1,
                      manufacturer: childDef?.manufacturer || 'Unknown',
                      type: childDef?.type || 'Unknown',
                      description: childDef?.description || ''
                    });
                  }
                }
              });
            }
          });
        }
        
        if (salvageItem) {
          // Generate a better display name for ToolArm salvage equipment
          let displayName = salvageItem?.localizedName || '';
          
          // If it's "Unknown Item" or empty, generate from slot/item name
          if (!displayName || displayName === 'Unknown Item') {
            // Try to extract from defaultItemDisplayName, slotName, or itemName
            const hpAny = hp as any;
            if (hpAny.defaultItemDisplayName && hpAny.defaultItemDisplayName !== 'Unknown Item') {
              displayName = `Salvage ${hpAny.defaultItemDisplayName}`;
            } else if (slotName.includes('left')) {
              displayName = 'Salvage Arm (Left)';
            } else if (slotName.includes('right')) {
              displayName = 'Salvage Arm (Right)';
            } else {
              displayName = 'Salvage Arm';
            }
          }
          
          equipment.push({
            hardpointId: hp.id,
            slotSize: hp.size || salvageItem?.size || 1,
            itemName: salvageItem?.itemName || hp.defaultItemName || '',
            displayName,
            manufacturer: salvageItem?.manufacturer || 'Unknown',
            grade: salvageItem?.grade || 3,
            powerUnits: salvageItem?.powerPipStats?.powerUnits || 1,
            health: salvageItem?.health || 0,
            type: 'salvage',
            salvageHead: salvageHeadInfo,
            modules: modulesInfo.length > 0 ? modulesInfo : undefined,
            // Add salvage stats (note: these are typically on the controller, not the head)
            salvageStats: {
              headCount: salvageItem?.salvageStats?.headCount,
              armingDistance: salvageItem?.salvageStats?.armingDistance,
              usesCargoGrid: salvageItem?.salvageStats?.usesCargoGrid
            }
          });
        }
      }
    });
    
    return equipment;
  }, [hardpoints, hardpointOverrides, loadedItemOverrides]);

  // Get total power budget from power plant(s)
  // Formula discovered via community reverse-engineering:
  // - Single plant: Total = plant_units (direct output)
  // - Multiple plants: Total = sum(socket_sizes) + Σ ceil(plant_units / socket_count)
  //   This provides redundancy bonus (base from sockets) but plants share load (division)
  const totalPips = useMemo(() => {
    const socketCount = powerPlantInfo.length;
    
    if (socketCount === 0) {
      console.log('=== POWER PLANT DEBUG: No power plants found, using default 14 ===');
      return 14; // Default fallback
    }
    
    console.log('=== POWER PLANT DEBUG ===');
    console.log(`Socket count: ${socketCount}, Power plants:`, powerPlantInfo);
    
    if (socketCount === 1) {
      // Single plant ship: Total = plant_units directly
      const units = powerPlantInfo[0]?.powerUnits || 14;
      console.log(`Single plant formula: units=${units}`);
      console.log(`TOTAL POWER PIPS: ${units}`);
      return units;
    }
    
    // Multi-plant ship formula:
    // Total = base (sum of socket sizes) + Σ ceil(plant_units / socket_count)
    
    // Calculate base from socket sizes
    const baseFromSockets = powerPlantInfo.reduce((sum, pp) => {
      console.log(`  Socket: hardpointId=${pp.hardpointId}, size=${pp.slotSize}`);
      return sum + pp.slotSize;
    }, 0);
    
    // Calculate plant contributions (each divided by socket count, ceiling'd)
    const plantContribution = powerPlantInfo.reduce((sum, pp) => {
      const contribution = Math.ceil(pp.powerUnits / socketCount);
      console.log(`  Plant: ${pp.displayName}, units=${pp.powerUnits}, contribution=ceil(${pp.powerUnits}/${socketCount})=${contribution}`);
      return sum + contribution;
    }, 0);
    
    const total = baseFromSockets + plantContribution;
    console.log(`Multi-plant formula: base=${baseFromSockets} + plantContribution=${plantContribution} = ${total}`);
    console.log(`TOTAL POWER PIPS: ${total}`);
    
    return total;
  }, [powerPlantInfo]);
  
  // Build components from hardpoints (with overrides applied)
  const initialComponents = useMemo(() => 
    buildPowerComponents(ship, hardpoints, totalPips, hardpointOverrides), 
    [ship, hardpoints, totalPips, hardpointOverrides]
  );
  
  const [components, setComponents] = useState<PowerComponent[]>(initialComponents);

  // Reset components when ship or overrides change
  useEffect(() => {
    setComponents(initialComponents);
    // Only reset mode/selection when ship actually changes, not on component swaps
  }, [initialComponents]);
  
  // Reset mode and selection only when ship changes (not on component swaps)
  useEffect(() => {
    setMode('scm'); // Reset to SCM mode when ship changes
    setSelectedComponent('power'); // Reset selection
  }, [ship.id]);

  // Sync power allocations to parent for signature/emissions calculations
  useEffect(() => {
    if (onPowerAllocationsChange) {
      const allocations: PowerAllocation[] = components.map(c => ({
        id: c.id,
        type: c.type,
        allocatedPips: c.allocatedPips,
        maxPips: c.maxPips,
        minPips: c.minPips,
        isOn: c.isOn,
        percentage: c.maxPips > 0 ? c.allocatedPips / c.maxPips : 0
      }));
      onPowerAllocationsChange(allocations);
    }
  }, [components, onPowerAllocationsChange]);

  // Calculate used pips (only from ON components that are active in current mode)
  const usedPips = useMemo(() => {
    return components
      .filter(c => {
        if (!c.isOn) return false;
        if (c.type === 'quantum') return false; // Quantum doesn't use pips
        if (mode === 'nav' && c.scmOnly) return false; // SCM-only components off in NAV
        if (mode === 'scm' && c.navOnly) return false; // NAV-only components off in SCM
        return true;
      })
      .reduce((sum, c) => sum + c.allocatedPips, 0);
  }, [components, mode]);

  const availablePips = totalPips - usedPips;

  // Load compatible power plants for a specific hardpoint size
  const loadCompatiblePowerPlants = useCallback(async (slotSize: number, hardpointId: number) => {
    setSelectedPowerPlantHardpoint(hardpointId);
    setLoadingPowerPlants(true);
    setShowPowerPlantDropdown(true);
    
    try {
      const plants = await componentService.getPowerPlantsBySize(slotSize);
      setAvailablePowerPlants(plants);
    } catch (err) {
      handleComponentLoadError(err, 'power plants');
      setAvailablePowerPlants([]);
    } finally {
      setLoadingPowerPlants(false);
    }
  }, []);

  // Handle power plant selection
  const handlePowerPlantSelect = useCallback((plant: ComponentItem) => {
    if (selectedPowerPlantHardpoint && onComponentChange) {
      onComponentChange(selectedPowerPlantHardpoint, plant.id);
    }
    setShowPowerPlantDropdown(false);
    setSelectedPowerPlantHardpoint(null);
  }, [selectedPowerPlantHardpoint, onComponentChange]);

  // Reset to original power plant
  const handleResetPowerPlant = useCallback((hardpointId: number) => {
    if (onComponentChange) {
      onComponentChange(hardpointId, null); // null = reset to original
    }
  }, [onComponentChange]);

  // Load compatible shields for a specific hardpoint size
  const loadCompatibleShields = useCallback(async (slotSize: number, hardpointId: number) => {
    setSelectedShieldHardpoint(hardpointId);
    setLoadingShields(true);
    setShowShieldDropdown(true);
    
    try {
      const shields = await componentService.getShieldsBySize(slotSize);
      setAvailableShields(shields);
    } catch (err) {
      handleComponentLoadError(err, 'shields');
      setAvailableShields([]);
    } finally {
      setLoadingShields(false);
    }
  }, []);

  // Handle shield selection
  const handleShieldSelect = useCallback((shield: ComponentItem) => {
    if (selectedShieldHardpoint && onComponentChange) {
      onComponentChange(selectedShieldHardpoint, shield.id);
    }
    setShowShieldDropdown(false);
    setSelectedShieldHardpoint(null);
  }, [selectedShieldHardpoint, onComponentChange]);

  // Reset to original shield
  const handleResetShield = useCallback((hardpointId: number) => {
    if (onComponentChange) {
      onComponentChange(hardpointId, null); // null = reset to original
    }
  }, [onComponentChange]);

  // Load compatible coolers for a specific hardpoint size
  const loadCompatibleCoolers = useCallback(async (slotSize: number, hardpointId: number) => {
    setSelectedCoolerHardpoint(hardpointId);
    setLoadingCoolers(true);
    setShowCoolerDropdown(true);
    
    try {
      const coolers = await componentService.getCoolersBySize(slotSize);
      setAvailableCoolers(coolers);
    } catch (err) {
      handleComponentLoadError(err, 'coolers');
      setAvailableCoolers([]);
    } finally {
      setLoadingCoolers(false);
    }
  }, []);

  // Handle cooler selection
  const handleCoolerSelect = useCallback((cooler: ComponentItem) => {
    if (selectedCoolerHardpoint && onComponentChange) {
      onComponentChange(selectedCoolerHardpoint, cooler.id);
    }
    setShowCoolerDropdown(false);
    setSelectedCoolerHardpoint(null);
  }, [selectedCoolerHardpoint, onComponentChange]);

  // Reset to original cooler
  const handleResetCooler = useCallback((hardpointId: number) => {
    if (onComponentChange) {
      onComponentChange(hardpointId, null); // null = reset to original
    }
  }, [onComponentChange]);

  // Load compatible quantum drives for a specific hardpoint size
  const loadCompatibleQuantumDrives = useCallback(async (slotSize: number) => {
    setLoadingQuantumDrives(true);
    setShowQuantumDropdown(true);
    
    try {
      const qds = await componentService.getQuantumDrivesBySize(slotSize);
      setAvailableQuantumDrives(qds);
    } catch (err) {
      handleComponentLoadError(err, 'quantum drives');
      setAvailableQuantumDrives([]);
    } finally {
      setLoadingQuantumDrives(false);
    }
  }, []);

  // Handle quantum drive selection
  const handleQuantumDriveSelect = useCallback((qd: ComponentItem) => {
    if (quantumDriveInfo && onComponentChange) {
      onComponentChange(quantumDriveInfo.hardpointId, qd.id);
    }
    setShowQuantumDropdown(false);
  }, [quantumDriveInfo, onComponentChange]);

  // Reset to original quantum drive
  const handleResetQuantumDrive = useCallback(() => {
    if (quantumDriveInfo && onComponentChange) {
      onComponentChange(quantumDriveInfo.hardpointId, null);
    }
  }, [quantumDriveInfo, onComponentChange]);

  // Load compatible radars for a specific hardpoint size
  const loadCompatibleRadars = useCallback(async (slotSize: number) => {
    setLoadingRadars(true);
    setShowRadarDropdown(true);
    
    try {
      const radars = await componentService.getRadarsBySize(slotSize);
      // Filter out placeholder items
      const filtered = radars.filter(r => {
        const name = (r.localizedName || r.itemName || '').toLowerCase();
        return !name.includes('placeholder');
      });
      setAvailableRadars(filtered);
    } catch (err) {
      handleComponentLoadError(err, 'radars');
      setAvailableRadars([]);
    } finally {
      setLoadingRadars(false);
    }
  }, []);

  // Handle radar selection
  const handleRadarSelect = useCallback((radar: ComponentItem) => {
    if (radarInfo && onComponentChange) {
      onComponentChange(radarInfo.hardpointId, radar.id);
    }
    setShowRadarDropdown(false);
  }, [radarInfo, onComponentChange]);

  // Reset to original radar
  const handleResetRadar = useCallback(() => {
    if (radarInfo && onComponentChange) {
      onComponentChange(radarInfo.hardpointId, null);
    }
  }, [radarInfo, onComponentChange]);

  // Load compatible mining lasers for a specific slot size
  const loadCompatibleMiningLasers = useCallback(async (slotSize: number, hardpointId: number, loadedItemId?: number) => {
    setLoadingMiningLasers(true);
    setShowMiningDropdown(true);
    setSelectedMiningHardpoint(hardpointId);
    setSelectedMiningLoadedItemId(loadedItemId || null);
    
    try {
      const lasers = await componentService.getMiningLasersBySize(slotSize);
      // Filter out placeholder items
      const filtered = lasers.filter(l => {
        const name = (l.localizedName || l.itemName || '').toLowerCase();
        return !name.includes('placeholder');
      });
      setAvailableMiningLasers(filtered);
    } catch (err) {
      handleComponentLoadError(err, 'mining lasers');
      setAvailableMiningLasers([]);
    } finally {
      setLoadingMiningLasers(false);
    }
  }, []);

  // Handle mining laser selection - swaps the loaded item (mining laser)
  const handleMiningLaserSelect = useCallback((laser: ComponentItem) => {
    if (selectedMiningHardpoint && selectedMiningLoadedItemId && onLoadedItemChange) {
      // Swap the loaded item (mining laser)
      onLoadedItemChange(selectedMiningHardpoint, selectedMiningLoadedItemId, laser.id);
    } else if (selectedMiningHardpoint && onComponentChange) {
      // Fallback: direct hardpoint swap (for direct mining lasers without nested structure)
      onComponentChange(selectedMiningHardpoint, laser.id);
    }
    setShowMiningDropdown(false);
    setSelectedMiningHardpoint(null);
    setSelectedMiningLoadedItemId(null);
  }, [selectedMiningHardpoint, selectedMiningLoadedItemId, onLoadedItemChange, onComponentChange]);

  // Reset to original mining laser
  const handleResetMiningLaser = useCallback((hardpointId: number, loadedItemId?: number) => {
    if (loadedItemId && onLoadedItemChange) {
      onLoadedItemChange(hardpointId, loadedItemId, null);
    } else if (onComponentChange) {
      onComponentChange(hardpointId, null);
    }
  }, [onComponentChange, onLoadedItemChange]);

  // Load available mining modules (gadgets like Surge, Stampede, etc.)
  const loadMiningModules = useCallback(async (hardpointId: number, laserSize: number) => {
    setLoadingMiningModules(true);
    setShowMiningModuleDropdown(true);
    setSelectedMiningModuleHardpoint(hardpointId);
    
    try {
      const modules = await componentService.getMiningModules();
      
      // Vehicle mining lasers (S0 - ROC, etc.) cannot use ship-based modules
      if (laserSize === 0) {
        setAvailableMiningModules([]);
        setLoadingMiningModules(false);
        return;
      }
      
      // Per P4K XML data: All ship mining laser module slots are MinSize=1, MaxSize=1
      // All mining modules are Size 1, so all modules fit all ship lasers (S1, S2, S3+)
      // Only filter out placeholders
      const filtered = modules.filter(m => {
        const name = (m.localizedName || m.itemName || '').toLowerCase();
        return !name.includes('placeholder');
      });
      
      setAvailableMiningModules(filtered);
    } catch (err) {
      handleComponentLoadError(err, 'mining modules');
      setAvailableMiningModules([]);
    } finally {
      setLoadingMiningModules(false);
    }
  }, []);

  // Handle mining module selection - add to the hardpoint's module list
  const handleMiningModuleSelect = useCallback((module: ComponentItem) => {
    if (selectedMiningModuleHardpoint) {
      setAddedMiningModules(prev => {
        const newMap = new Map(prev);
        const existing = newMap.get(selectedMiningModuleHardpoint) || [];
        newMap.set(selectedMiningModuleHardpoint, [...existing, module]);
        return newMap;
      });
    }
    setShowMiningModuleDropdown(false);
    setSelectedMiningModuleHardpoint(null);
  }, [selectedMiningModuleHardpoint]);

  // Remove a mining module from a hardpoint
  const handleRemoveMiningModule = useCallback((hardpointId: number, moduleIndex: number) => {
    setAddedMiningModules(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(hardpointId) || [];
      const updated = existing.filter((_, idx) => idx !== moduleIndex);
      if (updated.length === 0) {
        newMap.delete(hardpointId);
      } else {
        newMap.set(hardpointId, updated);
      }
      return newMap;
    });
  }, []);

  // Load compatible tractor beams for a specific hardpoint size
  const loadCompatibleTractorBeams = useCallback(async (slotSize: number, hardpointId: number) => {
    setLoadingTractorBeams(true);
    setShowTractorDropdown(true);
    setSelectedTractorHardpoint(hardpointId);
    
    try {
      const tractors = await componentService.getTractorBeamsBySize(slotSize);
      // Filter out placeholder items
      const filtered = tractors.filter(t => {
        const name = (t.localizedName || t.itemName || '').toLowerCase();
        return !name.includes('placeholder');
      });
      setAvailableTractorBeams(filtered);
    } catch (err) {
      handleComponentLoadError(err, 'tractor beams');
      setAvailableTractorBeams([]);
    } finally {
      setLoadingTractorBeams(false);
    }
  }, []);

  // Handle tractor beam selection
  const handleTractorBeamSelect = useCallback((tractor: ComponentItem) => {
    if (selectedTractorHardpoint && onComponentChange) {
      onComponentChange(selectedTractorHardpoint, tractor.id);
    }
    setShowTractorDropdown(false);
    setSelectedTractorHardpoint(null);
  }, [selectedTractorHardpoint, onComponentChange]);

  // Reset to original tractor beam
  const handleResetTractorBeam = useCallback((hardpointId: number) => {
    if (onComponentChange) {
      onComponentChange(hardpointId, null);
    }
  }, [onComponentChange]);

  // Load compatible salvage heads for a specific slot size
  const loadCompatibleSalvageHeads = useCallback(async (slotSize: number, hardpointId: number, loadedItemId?: number) => {
    setLoadingSalvageHeads(true);
    setShowSalvageDropdown(true);
    setSelectedSalvageHardpoint(hardpointId);
    setSelectedSalvageLoadedItemId(loadedItemId || null);
    
    try {
      const heads = await componentService.getSalvageHeadsBySize(slotSize);
      // Filter out placeholder items
      const filtered = heads.filter(h => {
        const name = (h.localizedName || h.itemName || '').toLowerCase();
        return !name.includes('placeholder');
      });
      setAvailableSalvageHeads(filtered);
    } catch (err) {
      handleComponentLoadError(err, 'salvage heads');
      setAvailableSalvageHeads([]);
    } finally {
      setLoadingSalvageHeads(false);
    }
  }, []);

  // Handle salvage head selection - swaps the loaded item (salvage head)
  const handleSalvageHeadSelect = useCallback((head: ComponentItem) => {
    if (selectedSalvageHardpoint && selectedSalvageLoadedItemId && onLoadedItemChange) {
      onLoadedItemChange(selectedSalvageHardpoint, selectedSalvageLoadedItemId, head.id);
    }
    setShowSalvageDropdown(false);
    setSelectedSalvageHardpoint(null);
    setSelectedSalvageLoadedItemId(null);
  }, [selectedSalvageHardpoint, selectedSalvageLoadedItemId, onLoadedItemChange]);

  // Reset to original salvage head
  const handleResetSalvageHead = useCallback((hardpointId: number, loadedItemId: number) => {
    if (onLoadedItemChange) {
      onLoadedItemChange(hardpointId, loadedItemId, null);
    }
  }, [onLoadedItemChange]);

  // Load available salvage modules (gadgets like Abrade, Cinch, etc.)
  const loadSalvageModules = useCallback(async (hardpointId: number) => {
    setLoadingSalvageModules(true);
    setShowSalvageModuleDropdown(true);
    setSelectedSalvageModuleHardpoint(hardpointId);
    
    try {
      const modules = await componentService.getSalvageModules();
      // Filter out placeholder items
      const filtered = modules.filter(m => {
        const name = (m.localizedName || m.itemName || '').toLowerCase();
        return !name.includes('placeholder');
      });
      setAvailableSalvageModules(filtered);
    } catch (err) {
      handleComponentLoadError(err, 'salvage modules');
      setAvailableSalvageModules([]);
    } finally {
      setLoadingSalvageModules(false);
    }
  }, []);

  // Handle salvage module selection - add to the hardpoint's module list
  const handleSalvageModuleSelect = useCallback((module: ComponentItem) => {
    if (selectedSalvageModuleHardpoint) {
      setAddedSalvageModules(prev => {
        const newMap = new Map(prev);
        const existing = newMap.get(selectedSalvageModuleHardpoint) || [];
        newMap.set(selectedSalvageModuleHardpoint, [...existing, module]);
        return newMap;
      });
    }
    setShowSalvageModuleDropdown(false);
    setSelectedSalvageModuleHardpoint(null);
  }, [selectedSalvageModuleHardpoint]);

  // Remove a salvage module from a hardpoint
  const handleRemoveSalvageModule = useCallback((hardpointId: number, moduleIndex: number) => {
    setAddedSalvageModules(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(hardpointId) || [];
      const updated = existing.filter((_, idx) => idx !== moduleIndex);
      if (updated.length === 0) {
        newMap.delete(hardpointId);
      } else {
        newMap.set(hardpointId, updated);
      }
      return newMap;
    });
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Use data attributes for click-outside detection (CSS module class names have invalid selector chars)
      if (!target.closest('[data-dropdown="powerplant"]') && !target.closest('[data-swap-button]')) {
        setShowPowerPlantDropdown(false);
        setSelectedPowerPlantHardpoint(null);
      }
      if (!target.closest('[data-dropdown="shield"]') && !target.closest('[data-swap-button]')) {
        setShowShieldDropdown(false);
        setSelectedShieldHardpoint(null);
      }
      if (!target.closest('[data-dropdown="cooler"]') && !target.closest('[data-swap-button]')) {
        setShowCoolerDropdown(false);
        setSelectedCoolerHardpoint(null);
      }
      if (!target.closest('[data-dropdown="quantum"]') && !target.closest('[data-swap-button]')) {
        setShowQuantumDropdown(false);
      }
      if (!target.closest('[data-dropdown="radar"]') && !target.closest('[data-swap-button]')) {
        setShowRadarDropdown(false);
      }
      // Utility equipment dropdowns
      if (!target.closest('[data-dropdown="mining"]') && !target.closest('[data-swap-button]')) {
        setShowMiningDropdown(false);
        setSelectedMiningHardpoint(null);
      }
      if (!target.closest('[data-dropdown="miningmodule"]') && !target.closest('[data-swap-button]')) {
        setShowMiningModuleDropdown(false);
      }
      if (!target.closest('[data-dropdown="tractor"]') && !target.closest('[data-swap-button]')) {
        setShowTractorDropdown(false);
        setSelectedTractorHardpoint(null);
      }
      if (!target.closest('[data-dropdown="salvage"]') && !target.closest('[data-swap-button]')) {
        setShowSalvageDropdown(false);
        setSelectedSalvageHardpoint(null);
      }
      if (!target.closest('[data-dropdown="salvagemodule"]') && !target.closest('[data-swap-button]')) {
        setShowSalvageModuleDropdown(false);
      }
    };
    
    if (showPowerPlantDropdown || showShieldDropdown || showCoolerDropdown || showQuantumDropdown || showRadarDropdown || showMiningDropdown || showMiningModuleDropdown || showTractorDropdown || showSalvageDropdown || showSalvageModuleDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showPowerPlantDropdown, showShieldDropdown, showCoolerDropdown, showQuantumDropdown, showRadarDropdown, showMiningDropdown, showMiningModuleDropdown, showTractorDropdown, showSalvageDropdown, showSalvageModuleDropdown]);

  // Handle mode change - adjust component states
  const handleModeChange = useCallback((newMode: 'scm' | 'nav') => {
    setMode(newMode);
    setComponents(prev => prev.map(c => {
      if (newMode === 'nav') {
        // NAV mode: Turn OFF weapons and shields, turn ON quantum
        if (c.scmOnly) return { ...c, isOn: false };
        if (c.navOnly) return { ...c, isOn: true };
      } else {
        // SCM mode: Turn ON weapons and shields, turn OFF quantum
        if (c.scmOnly) return { ...c, isOn: true };
        if (c.navOnly) return { ...c, isOn: false };
      }
      return c;
    }));
  }, []);

  const toggleComponent = useCallback((componentId: string) => {
    setComponents(prev => {
      const targetComponent = prev.find(c => c.id === componentId);
      if (!targetComponent) return prev;
      
      // When turning ON a utility mode (mining/tractor/salvage), turn OFF weapons
      // When turning OFF all utility modes, weapons become available again
      const isUtilityMode = targetComponent.utilityMode;
      const turningOn = !targetComponent.isOn;
      
      return prev.map(c => {
        if (c.id === componentId) {
          // Toggle the target component
          if (!c.isOn) {
            // Turning ON - check if we have enough power for minimum pips
            const currentUsed = prev
              .filter(comp => comp.isOn && comp.id !== componentId && comp.type !== 'quantum')
              .reduce((sum, comp) => sum + comp.allocatedPips, 0);
            const wouldNeed = currentUsed + c.minPips;
            
            if (wouldNeed > totalPips) {
              // Not enough power to turn on
              return c;
            }
            // Turn on with minimum pips (for utility modes, minPips = maxPips)
            return { ...c, isOn: true, allocatedPips: c.minPips };
          }
          // Turning off - return pips to pool
          return { ...c, isOn: false, allocatedPips: 0 };
        }
        
        // If turning ON a utility mode, disable weapons
        if (isUtilityMode && turningOn && c.type === 'weapon') {
          return { ...c, isOn: false, allocatedPips: 0 };
        }
        
        // If turning OFF a utility mode and no other utility modes are on,
        // we don't auto-enable weapons - user can do that manually
        
        return c;
      });
    });
  }, [totalPips]);

  const changePips = useCallback((componentId: string, newPips: number) => {
    setComponents(prev => {
      const component = prev.find(c => c.id === componentId);
      if (!component) return prev;
      
      // Calculate current available (excluding this component)
      const currentUsedExcludingThis = prev
        .filter(c => c.isOn && c.id !== componentId && c.type !== 'quantum')
        .reduce((sum, c) => sum + c.allocatedPips, 0);
      
      const maxCanAllocate = totalPips - currentUsedExcludingThis;
      
      // Clamp to valid range
      const clampedPips = Math.max(
        component.minPips, 
        Math.min(newPips, component.maxPips, maxCanAllocate)
      );
      
      return prev.map(c => 
        c.id === componentId ? { ...c, allocatedPips: clampedPips } : c
      );
    });
  }, [totalPips]);

  const selectedComponentData = components.find(c => c.id === selectedComponent);

  // Create power "component" for display
  const powerComponent: PowerComponent = {
    id: 'power',
    name: 'POWER',
    type: 'power',
    icon: <Zap size={18} />,
    maxPips: totalPips,
    minPips: 0,
    allocatedPips: usedPips, // Show how many are allocated OUT
    isOn: true
  };

  // Dynamic grid columns: 1 for power + component count
  const gridColumns = 1 + components.length;

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <h3>⚡ Power Management</h3>
        <span className={styles.budgetIndicator}>
          OUTPUT {usedPips} / {totalPips}
        </span>
      </div>

      {/* Mode Toggle - At top for visibility */}
      <div className={styles.modeToggle}>
        <button 
          className={`${styles.modeButton} ${mode === 'scm' ? styles.modeActive : ''}`}
          onClick={() => handleModeChange('scm')}
        >
          SCM MODE
        </button>
        <button 
          className={`${styles.modeButton} ${mode === 'nav' ? styles.modeActive : ''}`}
          onClick={() => handleModeChange('nav')}
        >
          NAV MODE
        </button>
      </div>

      {/* Power Budget Bar */}
      <div className={styles.budgetBar}>
        <div className={styles.budgetBarLabel}>
          <span>ALLOCATED</span>
          <span>{usedPips} pips</span>
        </div>
        <div className={styles.budgetBarTrack}>
          <div 
            className={styles.budgetBarFill}
            style={{ width: `${Math.min(100, (usedPips / totalPips) * 100)}%` }}
          />
        </div>
        <div className={styles.budgetBarLabel}>
          <span>AVAILABLE</span>
          <span>{availablePips} pips</span>
        </div>
      </div>

      {/* Component Columns - POWER on left, then systems */}
      <div className={styles.columnsScrollWrapper}>
        <div className={styles.columnsGrid}>
          {/* POWER column (source) */}
          <PipColumn
          component={powerComponent}
          isSelected={selectedComponent === 'power'}
          onSelect={() => setSelectedComponent('power')}
          onToggle={() => {}}
          onPipChange={() => {}}
          disabled={false}
          readOnly={true}
          availablePips={availablePips}
          isPowerColumn={true}
        />
        
        {/* Consumer columns */}
        {components.map(component => {
          // Check if any utility mode is active
          const utilityModeActive = components.some(c => c.utilityMode && c.isOn);
          
          // Determine if disabled based on mode
          // Weapons are disabled when in NAV mode OR when any utility mode is active
          const isDisabled = (mode === 'nav' && component.scmOnly) || 
                            (mode === 'scm' && component.navOnly) ||
                            (component.type === 'weapon' && utilityModeActive);
          return (
            <PipColumn
              key={component.id}
              component={component}
              isSelected={selectedComponent === component.id}
              onSelect={() => setSelectedComponent(component.id)}
              onToggle={() => toggleComponent(component.id)}
              onPipChange={(pips) => changePips(component.id, pips)}
              disabled={isDisabled}
              readOnly={readOnly}
              availablePips={availablePips}
            />
          );
        })}
        </div>
      </div>

      {/* Selected Component Details */}
      {selectedComponent === 'power' ? (
        <div className={styles.systemDetails}>
          <div className={styles.systemDetailsHeader}>
            <span className={`${styles.systemDetailsIcon} ${styles.powerIcon}`}><Zap size={20} /></span>
            <span className={styles.systemDetailsName}>POWER PLANT{powerPlantInfo.length > 1 ? 'S' : ''}</span>
            {/* Reset All Button - show if any overrides exist */}
            {!readOnly && hardpointOverrides && hardpointOverrides.size > 0 && onResetAll && (
              <button 
                className={styles.resetAllButton}
                onClick={onResetAll}
                title="Reset all to stock defaults"
              >
                <RefreshCw size={14} />
                <span>Reset All</span>
              </button>
            )}
          </div>
          
          <div className={styles.pipCounter}>
            <span className={styles.pipCounterValue}>{availablePips}</span>
            <span className={styles.pipCounterSeparator}>/</span>
            <span className={styles.pipCounterMax}>{totalPips} available</span>
          </div>

          {/* Power Plant List with Swap Capability */}
          <div className={styles.powerPlantList}>
            {powerPlantInfo.map((plant, idx) => {
              const isOverridden = hardpointOverrides?.has(plant.hardpointId);
              const isSelected = selectedPowerPlantHardpoint === plant.hardpointId;
              
              return (
                <div 
                  key={plant.hardpointId} 
                  className={`${styles.powerPlantItem} ${isOverridden ? styles.powerPlantModified : ''}`}
                >
                  <div className={styles.powerPlantHeader}>
                    <div className={styles.powerPlantSlot}>
                      <span className={styles.slotLabel}>Slot {idx + 1}</span>
                      <span className={styles.slotSize}>Size {plant.slotSize}</span>
                    </div>
                    {isOverridden && (
                      <button 
                        className={styles.resetButton}
                        onClick={() => handleResetPowerPlant(plant.hardpointId)}
                        title="Reset to original"
                      >
                        <RefreshCw size={14} />
                      </button>
                    )}
                  </div>
                  
                  <div className={styles.powerPlantDetails}>
                    <span className={styles.powerPlantName}>{plant.displayName}</span>
                    <div className={styles.powerPlantMeta}>
                      <span className={styles.powerPlantMfg}>{plant.manufacturer}</span>
                      <span className={styles.powerPlantSize}>S{plant.size}</span>
                      <span className={styles.powerPlantGrade}>Grade {['A', 'B', 'C', 'D'][plant.grade - 1] || 'C'}</span>
                      {plant.classification && (
                        <span className={styles.powerPlantType}>{plant.classification}</span>
                      )}
                    </div>
                    <div className={styles.powerPlantStats}>
                      <span className={styles.statItem}>
                        <Zap size={12} />
                        {plant.powerUnits} pips
                      </span>
                      {/* powerOutput is the same pip figure already shown above -
                          the old /1000 "kW" rendering was a wrong unit (0.0kW) */}
                      {plant.health > 0 && (
                        <span className={styles.statItem}>
                          {plant.health >= 1000 ? `${(plant.health / 1000).toFixed(1)}k` : plant.health} HP
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {!readOnly && (
                    <div className={styles.swapContainer}>
                      <button 
                        data-swap-button
                        className={`${styles.swapButton} ${isSelected ? styles.swapButtonActive : ''}`}
                        onClick={() => loadCompatiblePowerPlants(plant.slotSize, plant.hardpointId)}
                      >
                        <span>Swap</span>
                        <ChevronDown size={14} />
                      </button>
                      
                      {/* Power Plant Dropdown */}
                      {showPowerPlantDropdown && isSelected && (
                        <div className={styles.powerPlantDropdown} data-dropdown="powerplant">
                          <div className={styles.dropdownHeader}>
                            Size {plant.slotSize} Power Plants
                          </div>
                          {loadingPowerPlants ? (
                            <div className={styles.dropdownLoading}>
                              <RefreshCw size={16} className={styles.spinner} />
                              Loading...
                            </div>
                          ) : availablePowerPlants.length === 0 ? (
                            <div className={styles.dropdownEmpty}>
                              No compatible power plants found
                            </div>
                          ) : (
                            <div className={styles.dropdownList}>
                              {availablePowerPlants.map(pp => {
                                const isEquipped = pp.itemName === plant.itemName;
                                const parsed = parseComponentDescription(pp.description);
                                return (
                                  <button
                                    key={pp.id}
                                    className={`${styles.dropdownItem} ${isEquipped ? styles.dropdownItemEquipped : ''}`}
                                    onClick={() => handlePowerPlantSelect(pp)}
                                  >
                                    <div className={styles.dropdownItemMain}>
                                      <span className={styles.dropdownItemName}>
                                        {pp.localizedName}
                                      </span>
                                      <div className={styles.dropdownItemMeta}>
                                        <span className={styles.dropdownItemMfg}>
                                          {parsed.manufacturer || pp.manufacturer}
                                        </span>
                                        <span className={styles.dropdownItemSize}>S{pp.size}</span>
                                        <span className={styles.dropdownItemGrade}>
                                          Grade {['A', 'B', 'C', 'D'][pp.grade - 1] || '?'}
                                        </span>
                                        {parsed.classification && (
                                          <span className={styles.dropdownItemType}>
                                            {parsed.classification}
                                          </span>
                                        )}
                                        {pp.health > 0 && (
                                          <span className={styles.dropdownItemHP}>
                                            {pp.health >= 1000 ? `${(pp.health / 1000).toFixed(1)}k` : pp.health} HP
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className={styles.dropdownItemStats}>
                                      <span className={styles.dropdownItemPips}>
                                        {pp.powerPipStats?.powerUnits || '?'} pips
                                      </span>
                                    </div>
                                    {isEquipped && (
                                      <Check size={16} className={styles.equippedCheck} />
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          {powerPlantInfo.length > 1 && (
            <div className={styles.multiPlantNote}>
              Multiple power plants provide redundancy. Total pips = base from sockets + plant contributions.
            </div>
          )}
        </div>
      ) : selectedComponent === 'shields' && shieldInfo.length > 0 ? (
        <div className={styles.systemDetails}>
          <div className={styles.systemDetailsHeader}>
            <span className={`${styles.systemDetailsIcon} ${styles.shieldIcon}`}><Shield size={20} /></span>
            <span className={styles.systemDetailsName}>SHIELD{shieldInfo.length > 1 ? 'S' : ''}</span>
            <span className={styles.scmBadge}>SCM Only</span>
          </div>
          
          <div className={styles.pipCounter}>
            <span className={styles.pipCounterValue}>
              {shieldInfo.reduce((sum, s) => sum + s.powerUnits, 0)}
            </span>
            <span className={styles.pipCounterSeparator}>/</span>
            <span className={styles.pipCounterMax}>
              {shieldInfo.reduce((sum, s) => sum + s.powerUnits, 0)} pips total
            </span>
          </div>

          {/* Shield List with Swap Capability */}
          <div className={styles.powerPlantList}>
            {shieldInfo.map((shield, idx) => {
              const isOverridden = hardpointOverrides?.has(shield.hardpointId);
              const isSelected = selectedShieldHardpoint === shield.hardpointId;
              
              return (
                <div 
                  key={shield.hardpointId} 
                  className={`${styles.powerPlantItem} ${isOverridden ? styles.powerPlantModified : ''}`}
                >
                  <div className={styles.powerPlantHeader}>
                    <div className={styles.powerPlantSlot}>
                      <span className={styles.slotLabel}>Slot {idx + 1}</span>
                      <span className={styles.slotSize}>Size {shield.slotSize}</span>
                    </div>
                    {isOverridden && (
                      <button 
                        className={styles.resetButton}
                        onClick={() => handleResetShield(shield.hardpointId)}
                        title="Reset to original"
                      >
                        <RefreshCw size={14} />
                      </button>
                    )}
                  </div>
                  
                  <div className={styles.powerPlantDetails}>
                    <span className={styles.powerPlantName}>{shield.displayName}</span>
                    <div className={styles.powerPlantMeta}>
                      <span className={styles.powerPlantMfg}>{shield.manufacturer}</span>
                      <span className={styles.powerPlantSize}>S{shield.size}</span>
                      <span className={styles.powerPlantGrade}>Grade {['A', 'B', 'C', 'D'][shield.grade - 1] || 'C'}</span>
                      {shield.classification && (
                        <span className={styles.powerPlantType}>{shield.classification}</span>
                      )}
                    </div>
                    <div className={styles.powerPlantStats}>
                      <span className={styles.statItem}>
                        <Shield size={12} />
                        {shield.shieldHealthMax ? `${(shield.shieldHealthMax / 1000).toFixed(1)}k Shield` : '? Shield'}
                      </span>
                      {shield.regenRate && (
                        <span className={styles.statItem}>
                          {shield.regenRate.toFixed(0)}/s regen
                        </span>
                      )}
                      {shield.health > 0 && (
                        <span className={styles.statItem}>
                          {shield.health >= 1000 ? `${(shield.health / 1000).toFixed(1)}k` : shield.health} HP
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {!readOnly && (
                    <div className={styles.swapContainer}>
                      <button 
                        data-swap-button className={`${styles.swapButton} ${isSelected ? styles.swapButtonActive : ''}`}
                        onClick={() => {
                          if (isSelected && showShieldDropdown) {
                            setShowShieldDropdown(false);
                            setSelectedShieldHardpoint(null);
                          } else {
                            loadCompatibleShields(shield.slotSize, shield.hardpointId);
                          }
                        }}
                      >
                        <span>Swap</span>
                        <ChevronDown size={14} />
                      </button>
                      
                      {showShieldDropdown && isSelected && (
                        <div className={styles.shieldDropdown} data-dropdown="shield">
                          <div className={styles.dropdownHeader}>
                            Size {shield.slotSize} Shields
                          </div>
                          {loadingShields ? (
                            <div className={styles.dropdownLoading}>
                              <RefreshCw size={16} className={styles.spinner} />
                              Loading...
                            </div>
                          ) : availableShields.length === 0 ? (
                            <div className={styles.dropdownEmpty}>
                              No compatible shields found
                            </div>
                          ) : (
                            <div className={styles.dropdownList}>
                              {availableShields.map(sh => {
                                const isEquipped = sh.itemName === shield.itemName;
                                const parsed = parseComponentDescription(sh.description);
                                return (
                                  <button
                                    key={sh.id}
                                    className={`${styles.dropdownItem} ${isEquipped ? styles.dropdownItemEquipped : ''}`}
                                    onClick={() => handleShieldSelect(sh)}
                                  >
                                    <div className={styles.dropdownItemMain}>
                                      <span className={styles.dropdownItemName}>
                                        {sh.localizedName}
                                      </span>
                                      <div className={styles.dropdownItemMeta}>
                                        <span className={styles.dropdownItemMfg}>
                                          {parsed.manufacturer || sh.manufacturer}
                                        </span>
                                        <span className={styles.dropdownItemSize}>S{sh.size}</span>
                                        <span className={styles.dropdownItemGrade}>
                                          Grade {['A', 'B', 'C', 'D'][sh.grade - 1] || '?'}
                                        </span>
                                        {parsed.classification && (
                                          <span className={styles.dropdownItemType}>
                                            {parsed.classification}
                                          </span>
                                        )}
                                        {sh.health > 0 && (
                                          <span className={styles.dropdownItemHP}>
                                            {sh.health >= 1000 ? `${(sh.health / 1000).toFixed(1)}k` : sh.health} HP
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className={styles.dropdownItemStats}>
                                      <span className={styles.dropdownItemPips}>
                                        {sh.shieldStats?.shieldHealthMax ? `${(sh.shieldStats.shieldHealthMax / 1000).toFixed(1)}k Shield` : '?'}
                                      </span>
                                      {sh.shieldStats?.regenRate && (
                                        <span className={styles.dropdownItemPips}>
                                          {sh.shieldStats.regenRate.toFixed(0)}/s
                                        </span>
                                      )}
                                    </div>
                                    {isEquipped && (
                                      <Check size={16} className={styles.equippedCheck} />
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          {shieldInfo.length > 1 && (
            <div className={styles.multiPlantNote}>
              Multiple shields share a single power pool. Total pips = sum of all shield generators.
            </div>
          )}
        </div>
      ) : selectedComponentData?.type === 'cooler' ? (
        /* COOLER DETAILS - with swap capability */
        (() => {
          // Find the matching cooler info by hardpointId
          const cooler = coolerInfo.find(c => c.hardpointId === selectedComponentData.hardpointId);
          if (!cooler) return null;
          
          const isOverridden = hardpointOverrides?.has(cooler.hardpointId);
          const isSelected = selectedCoolerHardpoint === cooler.hardpointId;
          const coolerIndex = coolerInfo.indexOf(cooler);
          
          return (
            <div className={styles.systemDetails}>
              <div className={styles.systemDetailsHeader}>
                <span className={`${styles.systemDetailsIcon} ${styles.coolerIcon}`}><Thermometer size={20} /></span>
                <span className={styles.systemDetailsName}>
                  {coolerInfo.length > 1 ? `COOLER ${coolerIndex + 1}` : 'COOLER'}
                </span>
                {/* Reset All Button - show if any overrides exist */}
                {!readOnly && hardpointOverrides && hardpointOverrides.size > 0 && onResetAll && (
                  <button 
                    className={styles.resetAllButton}
                    onClick={onResetAll}
                    title="Reset all to stock defaults"
                  >
                    <RefreshCw size={14} />
                    <span>Reset All</span>
                  </button>
                )}
              </div>
              
              <div className={styles.pipCounter}>
                <span className={styles.pipCounterValue}>{cooler.powerUnits}</span>
                <span className={styles.pipCounterSeparator}>/</span>
                <span className={styles.pipCounterMax}>{cooler.powerUnits} pips</span>
              </div>
              
              {/* Cooler with Swap Capability */}
              <div className={styles.powerPlantList}>
                <div 
                  className={`${styles.powerPlantItem} ${isOverridden ? styles.powerPlantModified : ''}`}
                >
                  <div className={styles.powerPlantHeader}>
                    <div className={styles.powerPlantSlot}>
                      <span className={styles.slotLabel}>Slot {coolerIndex + 1}</span>
                      <span className={styles.slotSize}>Size {cooler.slotSize}</span>
                    </div>
                    {isOverridden && (
                      <button 
                        className={styles.resetButton}
                        onClick={() => handleResetCooler(cooler.hardpointId)}
                        title="Reset to original"
                      >
                        <RefreshCw size={14} />
                      </button>
                    )}
                  </div>
                  
                  <div className={styles.powerPlantDetails}>
                    <span className={styles.powerPlantName}>{cooler.displayName}</span>
                    <div className={styles.powerPlantMeta}>
                      <span className={styles.powerPlantMfg}>{cooler.manufacturer}</span>
                      <span className={styles.powerPlantSize}>S{cooler.size}</span>
                      <span className={styles.powerPlantGrade}>Grade {['A', 'B', 'C', 'D'][cooler.grade - 1] || 'C'}</span>
                      {cooler.classification && (
                        <span className={styles.powerPlantType}>{cooler.classification}</span>
                      )}
                    </div>
                    <div className={styles.powerPlantStats}>
                      <span className={styles.statItem}>
                        <Thermometer size={12} />
                        {cooler.coolingRate ? `${(cooler.coolingRate / 1000).toFixed(1)}k/s` : '? rate'}
                      </span>
                      {cooler.health > 0 && (
                        <span className={styles.statItem}>
                          {cooler.health >= 1000 ? `${(cooler.health / 1000).toFixed(1)}k` : cooler.health} HP
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {!readOnly && (
                    <div className={styles.swapContainer}>
                      <button 
                        data-swap-button className={`${styles.swapButton} ${isSelected ? styles.swapButtonActive : ''}`}
                        onClick={() => {
                          if (isSelected && showCoolerDropdown) {
                            setShowCoolerDropdown(false);
                            setSelectedCoolerHardpoint(null);
                          } else {
                            loadCompatibleCoolers(cooler.slotSize, cooler.hardpointId);
                          }
                        }}
                      >
                        <span>Swap</span>
                        <ChevronDown size={14} />
                      </button>
                      
                      {showCoolerDropdown && isSelected && (
                        <div className={styles.coolerDropdown} data-dropdown="cooler">
                          <div className={styles.dropdownHeader}>
                            Size {cooler.slotSize} Coolers
                          </div>
                          {loadingCoolers ? (
                            <div className={styles.dropdownLoading}>
                              <RefreshCw size={16} className={styles.spinner} />
                              Loading...
                            </div>
                          ) : availableCoolers.length === 0 ? (
                            <div className={styles.dropdownEmpty}>
                              No compatible coolers found
                            </div>
                          ) : (
                            <div className={styles.dropdownList}>
                              {availableCoolers.map(cl => {
                                const isEquipped = cl.itemName === cooler.itemName;
                                const parsed = parseComponentDescription(cl.description);
                                return (
                                  <button
                                    key={cl.id}
                                    className={`${styles.dropdownItem} ${isEquipped ? styles.dropdownItemEquipped : ''}`}
                                    onClick={() => handleCoolerSelect(cl)}
                                  >
                                    <div className={styles.dropdownItemMain}>
                                      <span className={styles.dropdownItemName}>
                                        {cl.localizedName}
                                      </span>
                                      <div className={styles.dropdownItemMeta}>
                                        <span className={styles.dropdownItemMfg}>
                                          {parsed.manufacturer || cl.manufacturer}
                                        </span>
                                        <span className={styles.dropdownItemSize}>S{cl.size}</span>
                                        <span className={styles.dropdownItemGrade}>
                                          Grade {['A', 'B', 'C', 'D'][cl.grade - 1] || '?'}
                                        </span>
                                        {parsed.classification && (
                                          <span className={styles.dropdownItemType}>
                                            {parsed.classification}
                                          </span>
                                        )}
                                        {cl.health > 0 && (
                                          <span className={styles.dropdownItemHP}>
                                            {cl.health >= 1000 ? `${(cl.health / 1000).toFixed(1)}k` : cl.health} HP
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className={styles.dropdownItemStats}>
                                      <span className={styles.dropdownItemPips}>
                                        {cl.coolerStats?.coolingRate ? `${(cl.coolerStats.coolingRate / 1000).toFixed(1)}k/s` : '?'}
                                      </span>
                                    </div>
                                    {isEquipped && (
                                      <Check size={16} className={styles.equippedCheck} />
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()
      ) : selectedComponentData?.type === 'quantum' && quantumDriveInfo ? (
        /* QUANTUM DRIVE DETAILS - with swap capability */
        (() => {
          const isOverridden = hardpointOverrides?.has(quantumDriveInfo.hardpointId);
          
          return (
            <div className={styles.systemDetails}>
              <div className={styles.systemDetailsHeader}>
                <span className={`${styles.systemDetailsIcon} ${styles.quantumIcon}`}><Gauge size={20} /></span>
                <span className={styles.systemDetailsName}>QUANTUM DRIVE</span>
                <span className={styles.navBadge}>NAV Only</span>
                {/* Reset All Button - show if any overrides exist */}
                {!readOnly && hardpointOverrides && hardpointOverrides.size > 0 && onResetAll && (
                  <button 
                    className={styles.resetAllButton}
                    onClick={onResetAll}
                    title="Reset all to stock defaults"
                  >
                    <RefreshCw size={14} />
                    <span>Reset All</span>
                  </button>
                )}
              </div>
              
              <p className={styles.systemDescription}>
                Quantum drive (continuous power, not pip-based)
              </p>
              
              {/* Quantum Drive with Swap Capability */}
              <div className={styles.powerPlantList}>
                <div 
                  className={`${styles.powerPlantItem} ${isOverridden ? styles.powerPlantModified : ''}`}
                >
                  <div className={styles.powerPlantHeader}>
                    <div className={styles.powerPlantSlot}>
                      <span className={styles.slotLabel}>Slot 1</span>
                      <span className={styles.slotSize}>Size {quantumDriveInfo.slotSize}</span>
                    </div>
                    {isOverridden && (
                      <button 
                        className={styles.resetButton}
                        onClick={handleResetQuantumDrive}
                        title="Reset to original"
                      >
                        <RefreshCw size={14} />
                      </button>
                    )}
                  </div>
                  
                  <div className={styles.powerPlantDetails}>
                    <span className={styles.powerPlantName}>{quantumDriveInfo.displayName}</span>
                    <div className={styles.powerPlantMeta}>
                      <span className={styles.powerPlantMfg}>{quantumDriveInfo.manufacturer}</span>
                      <span className={styles.powerPlantSize}>S{quantumDriveInfo.size}</span>
                      <span className={styles.powerPlantGrade}>Grade {['A', 'B', 'C', 'D'][quantumDriveInfo.grade - 1] || 'C'}</span>
                      {quantumDriveInfo.classification && (
                        <span className={styles.powerPlantType}>{quantumDriveInfo.classification}</span>
                      )}
                    </div>
                    <div className={styles.powerPlantStats}>
                      <span className={styles.statItem}>
                        <Gauge size={12} />
                        {quantumDriveInfo.driveSpeed ? `${(quantumDriveInfo.driveSpeed / 1000000).toFixed(1)}Mm/s` : '? speed'}
                      </span>
                      {quantumDriveInfo.spoolUpTime && (
                        <span className={styles.statItem}>
                          {quantumDriveInfo.spoolUpTime.toFixed(1)}s spool
                        </span>
                      )}
                      {quantumDriveInfo.cooldownTime && (
                        <span className={styles.statItem}>
                          {quantumDriveInfo.cooldownTime.toFixed(1)}s cooldown
                        </span>
                      )}
                      {quantumDriveInfo.health > 0 && (
                        <span className={styles.statItem}>
                          {quantumDriveInfo.health >= 1000 ? `${(quantumDriveInfo.health / 1000).toFixed(1)}k` : quantumDriveInfo.health} HP
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {!readOnly && (
                    <div className={styles.swapContainer}>
                      <button 
                        data-swap-button className={`${styles.swapButton} ${showQuantumDropdown ? styles.swapButtonActive : ''}`}
                        onClick={() => {
                          if (showQuantumDropdown) {
                            setShowQuantumDropdown(false);
                          } else {
                            loadCompatibleQuantumDrives(quantumDriveInfo.slotSize);
                          }
                        }}
                      >
                        <span>Swap</span>
                        <ChevronDown size={14} />
                      </button>
                      
                      {showQuantumDropdown && (
                        <div className={styles.quantumDropdown} data-dropdown="quantum">
                          <div className={styles.dropdownHeader}>
                            Size {quantumDriveInfo.slotSize} Quantum Drives
                          </div>
                          {loadingQuantumDrives ? (
                            <div className={styles.dropdownLoading}>
                              <RefreshCw size={16} className={styles.spinner} />
                              Loading...
                            </div>
                          ) : availableQuantumDrives.length === 0 ? (
                            <div className={styles.dropdownEmpty}>
                              No compatible quantum drives found
                            </div>
                          ) : (
                            <div className={styles.dropdownList}>
                              {availableQuantumDrives.map(qd => {
                                const isEquipped = qd.itemName === quantumDriveInfo.itemName;
                                const parsed = parseComponentDescription(qd.description);
                                return (
                                  <button
                                    key={qd.id}
                                    className={`${styles.dropdownItem} ${isEquipped ? styles.dropdownItemEquipped : ''}`}
                                    onClick={() => handleQuantumDriveSelect(qd)}
                                  >
                                    <div className={styles.dropdownItemMain}>
                                      <span className={styles.dropdownItemName}>
                                        {qd.localizedName}
                                      </span>
                                      <div className={styles.dropdownItemMeta}>
                                        <span className={styles.dropdownItemMfg}>
                                          {parsed.manufacturer || qd.manufacturer}
                                        </span>
                                        <span className={styles.dropdownItemSize}>S{qd.size}</span>
                                        <span className={styles.dropdownItemGrade}>
                                          Grade {['A', 'B', 'C', 'D'][qd.grade - 1] || '?'}
                                        </span>
                                        {parsed.classification && (
                                          <span className={styles.dropdownItemType}>
                                            {parsed.classification}
                                          </span>
                                        )}
                                        {qd.health > 0 && (
                                          <span className={styles.dropdownItemHP}>
                                            {qd.health >= 1000 ? `${(qd.health / 1000).toFixed(1)}k` : qd.health} HP
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className={styles.dropdownItemStats}>
                                      <span className={styles.dropdownItemPips}>
                                        {qd.quantumDriveStats?.driveSpeed ? `${(qd.quantumDriveStats.driveSpeed / 1000000).toFixed(1)}Mm/s` : '?'}
                                      </span>
                                      {qd.quantumDriveStats?.spoolUpTime && (
                                        <span className={styles.dropdownItemPips}>
                                          {qd.quantumDriveStats.spoolUpTime.toFixed(1)}s spool
                                        </span>
                                      )}
                                      {qd.quantumDriveStats?.cooldownTime && (
                                        <span className={styles.dropdownItemPips}>
                                          {qd.quantumDriveStats.cooldownTime.toFixed(1)}s cool
                                        </span>
                                      )}
                                    </div>
                                    {isEquipped && (
                                      <Check size={16} className={styles.equippedCheck} />
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()
      ) : selectedComponentData?.type === 'radar' && radarInfo ? (
        /* RADAR DETAILS - with swap capability */
        (() => {
          const isOverridden = hardpointOverrides?.has(radarInfo.hardpointId);
          
          return (
            <div className={styles.systemDetails}>
              <div className={styles.systemDetailsHeader}>
                <span className={`${styles.systemDetailsIcon} ${styles.radarIcon}`}><Radio size={20} /></span>
                <span className={styles.systemDetailsName}>RADAR</span>
                {/* Reset All Button - show if any overrides exist */}
                {!readOnly && hardpointOverrides && hardpointOverrides.size > 0 && onResetAll && (
                  <button 
                    className={styles.resetAllButton}
                    onClick={onResetAll}
                    title="Reset all to stock defaults"
                  >
                    <RefreshCw size={14} />
                    <span>Reset All</span>
                  </button>
                )}
              </div>
              
              <div className={styles.powerPlantList}>
                <div 
                  className={`${styles.powerPlantItem} ${isOverridden ? styles.powerPlantModified : ''}`}
                >
                  <div className={styles.powerPlantHeader}>
                    <div className={styles.powerPlantSlot}>
                      <span className={styles.slotLabel}>Main</span>
                      <span className={styles.slotSize}>Size {radarInfo.slotSize}</span>
                    </div>
                    {isOverridden && (
                      <button 
                        className={styles.resetButton}
                        onClick={handleResetRadar}
                        title="Reset to original"
                      >
                        <RefreshCw size={14} />
                      </button>
                    )}
                  </div>
                  
                  <div className={styles.powerPlantDetails}>
                    <span className={styles.powerPlantName}>{radarInfo.displayName}</span>
                    <div className={styles.powerPlantMeta}>
                      <span className={styles.powerPlantMfg}>{radarInfo.manufacturer}</span>
                      <span className={styles.powerPlantGrade}>Grade {['A', 'B', 'C', 'D'][radarInfo.grade - 1] || 'C'}</span>
                      {radarInfo.classification && (
                        <span className={styles.powerPlantType}>{radarInfo.classification}</span>
                      )}
                    </div>
                    <div className={styles.powerPlantStats}>
                      <span className={styles.statItem}>
                        <Radio size={12} />
                        {radarInfo.powerUnits} pips
                      </span>
                      {radarInfo.health > 0 && (
                        <span className={styles.statItem}>
                          {radarInfo.health >= 1000 ? `${(radarInfo.health / 1000).toFixed(1)}k` : radarInfo.health} HP
                        </span>
                      )}
                      {radarInfo.sensitivity > 0 && (
                        <span className={styles.statItem}>
                          {(radarInfo.sensitivity * 100).toFixed(0)}% sensitivity
                        </span>
                      )}
                      {radarInfo.piercing > 0 && (
                        <span className={styles.statItem}>
                          {(radarInfo.piercing * 100).toFixed(0)}% piercing
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {!readOnly && (
                    <div className={styles.swapContainer}>
                      <button 
                        data-swap-button className={`${styles.swapButton} ${showRadarDropdown ? styles.swapButtonActive : ''}`}
                        onClick={() => {
                          if (showRadarDropdown) {
                            setShowRadarDropdown(false);
                          } else {
                            loadCompatibleRadars(radarInfo.slotSize);
                          }
                        }}
                      >
                        <span>Swap</span>
                        <ChevronDown size={14} />
                      </button>
                      
                      {showRadarDropdown && (
                        <div className={`${styles.powerPlantDropdown} ${styles.radarDropdown}`} data-dropdown="radar">
                          <div className={styles.dropdownHeader}>
                            Size {radarInfo.slotSize} Radars
                          </div>
                          {loadingRadars ? (
                            <div className={styles.dropdownLoading}>
                              <RefreshCw size={16} className={styles.spinner} />
                              Loading...
                            </div>
                          ) : availableRadars.length === 0 ? (
                            <div className={styles.dropdownEmpty}>
                              No compatible radars found
                            </div>
                          ) : (
                            <div className={styles.dropdownList}>
                              {availableRadars.map(r => {
                                const isEquipped = r.itemName === radarInfo.itemName;
                                const parsed = parseComponentDescription(r.description);
                                return (
                                  <button
                                    key={r.id}
                                    className={`${styles.dropdownItem} ${isEquipped ? styles.dropdownItemEquipped : ''}`}
                                    onClick={() => handleRadarSelect(r)}
                                  >
                                    <div className={styles.dropdownItemMain}>
                                      <span className={styles.dropdownItemName}>
                                        {r.localizedName}
                                      </span>
                                      <div className={styles.dropdownItemMeta}>
                                        <span className={styles.dropdownItemMfg}>
                                          {parsed.manufacturer || r.manufacturer}
                                        </span>
                                        <span className={styles.dropdownItemSize}>S{r.size}</span>
                                        <span className={styles.dropdownItemGrade}>
                                          Grade {['A', 'B', 'C', 'D'][r.grade - 1] || '?'}
                                        </span>
                                        {parsed.classification && (
                                          <span className={styles.dropdownItemType}>
                                            {parsed.classification}
                                          </span>
                                        )}
                                        {r.health > 0 && (
                                          <span className={styles.dropdownItemHP}>
                                            {r.health >= 1000 ? `${(r.health / 1000).toFixed(1)}k` : r.health} HP
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className={styles.dropdownItemStats}>
                                      <span className={styles.dropdownItemPips}>
                                        {r.powerPipStats?.powerUnits || '?'} pips
                                      </span>
                                      <span className={styles.dropdownItemPips}>
                                        {((r.radarStats?.sensitivity || 0) * 100).toFixed(0)}% sensitivity
                                      </span>
                                      <span className={styles.dropdownItemPips}>
                                        {((r.radarStats?.piercing || 0) * 100).toFixed(0)}% piercing
                                      </span>
                                    </div>
                                    {isEquipped && (
                                      <Check size={16} className={styles.equippedCheck} />
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()
      ) : selectedComponent === 'mining' && miningInfo.length > 0 ? (
        /* MINING EQUIPMENT DETAILS - shows all mining lasers with swap capability */
        <div className={styles.systemDetails}>
          <div className={styles.systemDetailsHeader}>
            <span className={`${styles.systemDetailsIcon} ${styles.miningIcon}`}><Pickaxe size={20} /></span>
            <span className={styles.systemDetailsName}>MINING{miningInfo.length > 1 ? ` (${miningInfo.length})` : ''}</span>
            <span className={styles.utilityBadge}>Utility Mode</span>
          </div>
          
          <div className={styles.pipCounter}>
            <span className={styles.pipCounterValue}>
              {miningInfo.reduce((sum, m) => sum + m.powerUnits, 0)}
            </span>
            <span className={styles.pipCounterSeparator}>/</span>
            <span className={styles.pipCounterMax}>
              {miningInfo.reduce((sum, m) => sum + m.powerUnits, 0)} pips total
            </span>
          </div>

          {/* Mining Laser List with Swap Capability */}
          <div className={styles.powerPlantList}>
            {miningInfo.map((mining, idx) => {
              // Check for override on hardpoint OR on loaded item (mining laser)
              const loadedOverrideKey = mining.miningLaser?.loadedItemId ? `${mining.hardpointId}:${mining.miningLaser.loadedItemId}` : null;
              const isOverridden = hardpointOverrides?.has(mining.hardpointId) || 
                                   (loadedOverrideKey ? loadedItemOverrides?.has(loadedOverrideKey) : false);
              const isSelected = selectedMiningHardpoint === mining.hardpointId;
              
              return (
                <div 
                  key={mining.hardpointId} 
                  className={`${styles.powerPlantItem} ${isOverridden ? styles.powerPlantModified : ''}`}
                >
                  <div className={styles.powerPlantHeader}>
                    <div className={styles.powerPlantSlot}>
                      <span className={styles.slotLabel}>Slot {idx + 1}</span>
                      <span className={styles.slotSize}>Size {mining.slotSize}</span>
                    </div>
                    {isOverridden && (
                      <button 
                        className={styles.resetButton}
                        onClick={() => handleResetMiningLaser(mining.hardpointId, mining.miningLaser?.loadedItemId)}
                        title="Reset to original"
                      >
                        <RefreshCw size={14} />
                      </button>
                    )}
                  </div>
                  
                  <div className={styles.powerPlantDetails}>
                    <span className={styles.powerPlantName}>{mining.displayName}</span>
                    <div className={styles.powerPlantMeta}>
                      <span className={styles.powerPlantMfg}>{mining.manufacturer}</span>
                      <span className={styles.powerPlantGrade}>Grade {['A', 'B', 'C', 'D'][mining.grade - 1] || 'C'}</span>
                    </div>
                    <div className={styles.powerPlantStats}>
                      <span className={styles.statItem}>
                        <Zap size={12} />
                        {mining.powerUnits} pips
                      </span>
                      {mining.health > 0 && (
                        <span className={styles.statItem}>
                          {mining.health >= 1000 ? `${(mining.health / 1000).toFixed(1)}k` : mining.health} HP
                        </span>
                      )}
                    </div>
                    {/* Show mining stats for direct mining lasers (no nested miningLaser) */}
                    {!mining.miningLaser && mining.miningStats && (
                      <div style={{ fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.7)', marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {mining.miningStats.power !== null && mining.miningStats.power !== undefined && mining.miningStats.power > 0 && (
                          <span><span style={{ color: '#ffa500' }}>Laser:</span> {mining.miningStats.power.toFixed(0)}</span>
                        )}
                        {mining.miningStats.resistance !== null && mining.miningStats.resistance !== undefined && (
                          <span><span style={{ color: '#ffa500' }}>Res:</span> {mining.miningStats.resistance.toFixed(1)}</span>
                        )}
                        {mining.miningStats.instability !== null && mining.miningStats.instability !== undefined && (
                          <span><span style={{ color: '#ffa500' }}>Inst:</span> {mining.miningStats.instability.toFixed(1)}</span>
                        )}
                        {mining.miningStats.optimalWindow !== null && mining.miningStats.optimalWindow !== undefined && (
                          <span><span style={{ color: '#ffa500' }}>Window:</span> {mining.miningStats.optimalWindow.toFixed(1)}</span>
                        )}
                        {mining.miningStats.range !== null && mining.miningStats.range !== undefined && mining.miningStats.range > 100 && (
                          <span><span style={{ color: '#ffa500' }}>Range:</span> {(mining.miningStats.range / 1000).toFixed(2)}km</span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Show mining laser attachment if present (for turrets/arms) */}
                  {mining.miningLaser && (
                    <div className={styles.attachmentSection}>
                      <div className={styles.attachmentHeader}>
                        <span className={styles.attachmentLabel}>Mining Laser:</span>
                      </div>
                      {(() => {
                        // Calculate modified stats based on all modules
                        const installedModules = mining.modules || [];
                        const addedModulesForHardpoint = addedMiningModules.get(mining.hardpointId) || [];
                        const allModules = [...installedModules, ...addedModulesForHardpoint];
                        
                        if (!mining.miningLaser) return null;
                        
                        // Start with base laser stats
                        const basePower = mining.miningLaser.power || 0;
                        const baseResistance = mining.miningLaser.resistance || 0;
                        const baseInstability = mining.miningLaser.instability || 0;
                        const baseWindow = mining.miningLaser.optimalWindow || 0;
                        const baseRange = mining.miningLaser.range || 0;
                        
                        // Separate passive and active modules
                        const passiveModules = allModules.filter(mod => {
                          const modAny = mod as any;
                          const itemName = (modAny.itemName || '').toLowerCase();
                          return itemName.includes('_passive_') || itemName.includes('passive');
                        });
                        const activeModules = allModules.filter(mod => {
                          const modAny = mod as any;
                          const itemName = (modAny.itemName || '').toLowerCase();
                          return itemName.includes('_active_') || itemName.includes('active');
                        });
                        
                        // Track PASSIVE module modifiers (applied to base stats)
                        let powerMultiplier = 0;
                        let resistanceBonus = 0;
                        let instabilityMultiplier = 0;
                        let instabilityBonus = 0;
                        let windowBonus = 0;
                        
                        // Track ACTIVE module modifiers (shown as potential)
                        const activePowerMods: string[] = [];
                        const activeResistanceMods: string[] = [];
                        const activeInstabilityMods: string[] = [];
                        const activeWindowMods: string[] = [];
                        
                        // Apply PASSIVE module modifiers
                        // Stats WITHOUT +/- are multipliers that stack additively (135% + 90% = 225%)
                        // Stats WITH +/- are additive bonuses to base value (+15% = base * 1.15)
                        passiveModules.forEach(mod => {
                          const modAny = mod as any;
                          const desc = modAny.description || modAny.localizedName || '';
                          const statPattern = /([A-Za-z\s]+):\s*([+-]?\d+\.?\d*)%/g;
                          let match;
                          while ((match = statPattern.exec(desc)) !== null) {
                            const label = match[1].trim().toLowerCase();
                            const rawValue = match[2];
                            const value = parseFloat(rawValue);
                            const hasSign = rawValue.startsWith('+') || rawValue.startsWith('-');
                            
                            if (label.includes('mining laser power') || (label.includes('laser') && label.includes('power') && !label.includes('extraction'))) {
                              powerMultiplier += (value / 100);
                            } else if (label.includes('resistance')) {
                              resistanceBonus += (value / 100);
                            } else if (label.includes('instability')) {
                              if (hasSign) {
                                instabilityBonus += (value / 100);
                              } else {
                                instabilityMultiplier += (value / 100);
                              }
                            } else if (label.includes('optimal') && label.includes('window')) {
                              windowBonus += (value / 100);
                            }
                          }
                        });
                        
                        // Collect ACTIVE module modifiers for display
                        activeModules.forEach(mod => {
                          const modAny = mod as any;
                          const desc = modAny.description || modAny.localizedName || '';
                          const statPattern = /([A-Za-z\s]+):\s*([+-]?\d+\.?\d*)%/g;
                          let match;
                          while ((match = statPattern.exec(desc)) !== null) {
                            const label = match[1].trim().toLowerCase();
                            const rawValue = match[2];
                            
                            if (label.includes('mining laser power') || (label.includes('laser') && label.includes('power') && !label.includes('extraction'))) {
                              activePowerMods.push(rawValue + '%');
                            } else if (label.includes('resistance')) {
                              activeResistanceMods.push(rawValue + '%');
                            } else if (label.includes('instability')) {
                              activeInstabilityMods.push(rawValue + '%');
                            } else if (label.includes('optimal') && label.includes('window')) {
                              activeWindowMods.push(rawValue + '%');
                            }
                          }
                        });
                        
                        // Calculate modified values (with PASSIVE modules only)
                        const modifiedPower = basePower * powerMultiplier;
                        const modifiedResistance = baseResistance + (baseResistance * resistanceBonus);
                        const modifiedInstability = instabilityMultiplier > 0 
                          ? baseInstability * instabilityMultiplier 
                          : baseInstability + (baseInstability * instabilityBonus);
                        const modifiedWindow = baseWindow + (baseWindow * windowBonus);
                        
                        const hasPassiveMods = passiveModules.length > 0;
                        const hasActiveMods = activeModules.length > 0;
                        const statColor = hasPassiveMods || hasActiveMods ? '#00ff80' : '#ffa500';
                        
                        // Display the total multiplier/bonus percentage
                        const powerMultiplierPercent = (powerMultiplier * 100);
                        const resMultiplierPercent = (resistanceBonus * 100);
                        const instMultiplierPercent = instabilityMultiplier > 0 
                          ? (instabilityMultiplier * 100) 
                          : (instabilityBonus * 100);
                        const windowMultiplierPercent = (windowBonus * 100);
                        
                        return (
                          <div className={styles.attachmentDetails}>
                            <span className={styles.attachmentName}>{mining.miningLaser.displayName}</span>
                            <div className={styles.attachmentMeta}>
                              <span className={styles.attachmentSize}>S{mining.miningLaser.size}</span>
                              <span className={styles.attachmentMfg}>{mining.miningLaser.manufacturer}</span>
                            </div>
                            {/* Mining laser stats with module modifications */}
                            <div style={{ fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.7)', marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                              {modifiedPower > 0 && (
                                <span>
                                  <span style={{ color: statColor }}>Laser:</span> {modifiedPower.toFixed(0)}
                                  {hasPassiveMods && <span style={{ color: '#00ff80', fontSize: '0.65rem', marginLeft: '3px' }}>(+{powerMultiplierPercent.toFixed(0)}%)</span>}
                                  {activePowerMods.length > 0 && (
                                    <span style={{ color: '#ffaa00', fontSize: '0.65rem', marginLeft: '3px' }}>
                                      ({activePowerMods.join(', ')})
                                    </span>
                                  )}
                                </span>
                              )}
                              {modifiedResistance !== null && modifiedResistance !== undefined && (
                                <span>
                                  <span style={{ color: statColor }}>Res:</span> {modifiedResistance.toFixed(1)}
                                  {hasPassiveMods && <span style={{ color: '#00ff80', fontSize: '0.65rem', marginLeft: '3px' }}>({resMultiplierPercent >= 0 ? '+' : ''}{resMultiplierPercent.toFixed(0)}%)</span>}
                                  {activeResistanceMods.length > 0 && (
                                    <span style={{ color: '#ffaa00', fontSize: '0.65rem', marginLeft: '3px' }}>
                                      ({activeResistanceMods.join(', ')})
                                    </span>
                                  )}
                                </span>
                              )}
                              {modifiedInstability !== null && modifiedInstability !== undefined && (
                                <span>
                                  <span style={{ color: statColor }}>Inst:</span> {modifiedInstability.toFixed(1)}
                                  {hasPassiveMods && <span style={{ color: '#00ff80', fontSize: '0.65rem', marginLeft: '3px' }}>({instMultiplierPercent >= 0 ? '+' : ''}{instMultiplierPercent.toFixed(0)}%)</span>}
                                  {activeInstabilityMods.length > 0 && (
                                    <span style={{ color: '#ffaa00', fontSize: '0.65rem', marginLeft: '3px' }}>
                                      ({activeInstabilityMods.join(', ')})
                                    </span>
                                  )}
                                </span>
                              )}
                              {modifiedWindow !== null && modifiedWindow !== undefined && (
                                <span>
                                  <span style={{ color: statColor }}>Window:</span> {modifiedWindow.toFixed(1)}
                                  {hasPassiveMods && <span style={{ color: '#00ff80', fontSize: '0.65rem', marginLeft: '3px' }}>({windowMultiplierPercent >= 0 ? '+' : ''}{windowMultiplierPercent.toFixed(0)}%)</span>}
                                  {activeWindowMods.length > 0 && (
                                    <span style={{ color: '#ffaa00', fontSize: '0.65rem', marginLeft: '3px' }}>
                                      ({activeWindowMods.join(', ')})
                                    </span>
                                  )}
                                </span>
                              )}
                              {baseRange > 100 && (
                                <span><span style={{ color: '#ffa500' }}>Range:</span> {(baseRange / 1000).toFixed(2)}km</span>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                      
                      {/* Swap button for mining laser */}
                      {!readOnly && (
                        <div className={styles.swapContainer}>
                          <button 
                            data-swap-button className={`${styles.swapButton} ${isSelected && showMiningDropdown ? styles.swapButtonActive : ''}`}
                            onClick={() => {
                              if (isSelected && showMiningDropdown) {
                                setShowMiningDropdown(false);
                                setSelectedMiningHardpoint(null);
                                setSelectedMiningLoadedItemId(null);
                              } else {
                                loadCompatibleMiningLasers(
                                  mining.miningLaser?.size || mining.slotSize, 
                                  mining.hardpointId,
                                  mining.miningLaser?.loadedItemId
                                );
                              }
                            }}
                          >
                            <span>Swap Laser</span>
                            <ChevronDown size={14} />
                          </button>
                          
                          {showMiningDropdown && isSelected && (
                            <div className={`${styles.powerPlantDropdown} ${styles.miningDropdown}`} data-dropdown="mining">
                              <div className={styles.dropdownHeader}>
                                Size {mining.miningLaser?.size || mining.slotSize} Mining Lasers
                              </div>
                              {loadingMiningLasers ? (
                                <div className={styles.dropdownLoading}>
                                  <RefreshCw size={16} className={styles.spinner} />
                                  Loading...
                                </div>
                              ) : availableMiningLasers.length === 0 ? (
                                <div className={styles.dropdownEmpty}>
                                  No compatible mining lasers found
                                </div>
                              ) : (
                                <div className={styles.dropdownList}>
                                  {availableMiningLasers.map(laser => {
                                    const isEquipped = laser.itemName === mining.miningLaser?.itemName;
                                    const parsed = parseComponentDescription(laser.description);
                                    return (
                                      <button
                                        key={laser.id}
                                        className={`${styles.dropdownItem} ${isEquipped ? styles.dropdownItemEquipped : ''}`}
                                        onClick={() => handleMiningLaserSelect(laser)}
                                      >
                                        <div className={styles.dropdownItemMain}>
                                          <span className={styles.dropdownItemName}>
                                            {laser.localizedName || 'Mining Laser'}
                                          </span>
                                          <div className={styles.dropdownItemMeta}>
                                            <span className={styles.dropdownItemMfg}>
                                              {parsed.manufacturer || laser.manufacturer}
                                            </span>
                                            <span className={styles.dropdownItemGrade}>
                                              Grade {['A', 'B', 'C', 'D'][laser.grade - 1] || '?'}
                                            </span>
                                            {laser.health > 0 && (
                                              <span className={styles.dropdownItemHP}>
                                                {laser.health >= 1000 ? `${(laser.health / 1000).toFixed(1)}k` : laser.health} HP
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                        <div className={styles.dropdownItemStats}>
                                          <span className={styles.dropdownItemPips}>
                                            {laser.powerPipStats?.powerUnits || '?'} pips
                                          </span>
                                          {/* Mining laser stats */}
                                          {laser.miningLaserStats && (
                                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)', marginTop: '4px' }}>
                                              {laser.miningLaserStats.power !== null && laser.miningLaserStats.power !== undefined && laser.miningLaserStats.power > 0 && (
                                                <span><span style={{ color: '#ffa500', opacity: 0.8 }}>Laser:</span> {laser.miningLaserStats.power.toFixed(0)}</span>
                                              )}
                                              {laser.miningLaserStats.resistance !== null && laser.miningLaserStats.resistance !== undefined && (
                                                <span><span style={{ color: '#ffa500', opacity: 0.8 }}>Res:</span> {laser.miningLaserStats.resistance.toFixed(1)}</span>
                                              )}
                                              {laser.miningLaserStats.instability !== null && laser.miningLaserStats.instability !== undefined && (
                                                <span><span style={{ color: '#ffa500', opacity: 0.8 }}>Inst:</span> {laser.miningLaserStats.instability.toFixed(1)}</span>
                                              )}
                                              {laser.miningLaserStats.optimalWindow !== null && laser.miningLaserStats.optimalWindow !== undefined && (
                                                <span><span style={{ color: '#ffa500', opacity: 0.8 }}>Window:</span> {laser.miningLaserStats.optimalWindow.toFixed(1)}</span>
                                              )}
                                              {laser.miningLaserStats.range !== null && laser.miningLaserStats.range !== undefined && laser.miningLaserStats.range > 100 && (
                                                <span><span style={{ color: '#ffa500', opacity: 0.8 }}>Range:</span> {(laser.miningLaserStats.range / 1000).toFixed(2)}km</span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                        {isEquipped && (
                                          <Check size={16} className={styles.equippedCheck} />
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Mining Modules Section - shows installed modules and Add Module button */}
                  {mining.miningLaser && (() => {
                    const moduleSlots = mining.miningLaser?.moduleSlots || 3;
                    const installedModules = mining.modules || [];
                    const addedModulesForHardpoint = addedMiningModules.get(mining.hardpointId) || [];
                    const totalModules = installedModules.length + addedModulesForHardpoint.length;
                    const hasAvailableSlots = totalModules < moduleSlots;
                    const isModuleDropdownOpen = showMiningModuleDropdown && selectedMiningModuleHardpoint === mining.hardpointId;
                    
                    return (
                      <div className={styles.attachmentSection}>
                        <div className={styles.attachmentHeader}>
                          <span className={styles.attachmentLabel}>
                            Modules ({totalModules}/{moduleSlots} slots)
                          </span>
                        </div>
                        
                        {/* Show installed modules from database */}
                        {installedModules.length > 0 && installedModules.map((mod, modIdx) => {
                          const modAny = mod as any;
                          const modParsed = parseComponentDescription(modAny.description || '');
                          // Extract ALL modifier values from description using regex
                          const desc = modAny.description || '';
                          const modifiers: { label: string; value: string }[] = [];
                          
                          // Regex to match stat lines like "Mining Laser Power: 115%" or "Resistance: +15%"
                          const statPattern = /([A-Za-z\s]+):\s*([+-]?\d+\.?\d*%)/g;
                          let match;
                          while ((match = statPattern.exec(desc)) !== null) {
                            let label = match[1].trim();
                            // Remove 'n' prefix from stat names (nShatter Damage -> Shatter Damage)
                            if (label.startsWith('n') && label.length > 1 && label[1] === label[1].toUpperCase()) {
                              label = label.substring(1);
                            }
                            const value = match[2];
                            // Include ALL stats (not filtering by type)
                            modifiers.push({ label, value });
                          }
                          
                          const itemName = (modAny.itemName || '').toLowerCase();
                          const isActive = itemName.includes('_active_') || itemName.includes('active');
                          const isPassive = itemName.includes('_passive_') || itemName.includes('passive');
                          
                          return (
                            <div key={`db-${modIdx}`} className={styles.attachmentDetails}>
                              <span className={styles.attachmentName}>
                                └ {mod.displayName}
                                {isActive && <span style={{ marginLeft: '0.3rem', padding: '1px 4px', fontSize: '0.6rem', background: 'rgba(255, 170, 0, 0.2)', color: '#ffaa00', borderRadius: '2px', fontWeight: 600 }}>ACTIVE</span>}
                                {isPassive && <span style={{ marginLeft: '0.3rem', padding: '1px 4px', fontSize: '0.6rem', background: 'rgba(0, 255, 128, 0.2)', color: '#00ff80', borderRadius: '2px', fontWeight: 600 }}>PASSIVE</span>}
                              </span>
                              <div className={styles.attachmentMeta}>
                                <span className={styles.attachmentSize}>S{mod.size}</span>
                                <span className={styles.attachmentMfg}>{modParsed.manufacturer || mod.manufacturer || 'Mining Module'}</span>
                                {modAny.grade && <span className={styles.attachmentGrade}>Grade {['A', 'B', 'C', 'D'][modAny.grade - 1] || 'A'}</span>}
                              </div>
                              {/* Show module modifier values */}
                              {modifiers.length > 0 && (
                                <div style={{ fontSize: '0.65rem', color: 'rgba(100, 255, 150, 0.8)', marginTop: '4px' }}>
                                  {modifiers.map((mod, i) => (
                                    <div key={i} style={{ marginBottom: i < modifiers.length - 1 ? '2px' : 0 }}>
                                      {mod.label}: {mod.value}
                                    </div>
                                  ))}
                                </div>
                              )}
                              {modifiers.length === 0 && (
                                <div style={{ fontSize: '0.65rem', color: 'rgba(100, 200, 255, 0.7)', marginTop: '4px', fontStyle: 'italic' }}>
                                  Performance Modifier
                                </div>
                              )}
                            </div>
                          );
                        })}
                        
                        {/* Show user-added modules */}
                        {addedModulesForHardpoint.map((mod, modIdx) => {
                          const modParsed = parseComponentDescription(mod.description || '');
                          const itemName = (mod.itemName || '').toLowerCase();
                          const isActive = itemName.includes('_active_') || itemName.includes('active');
                          const isPassive = itemName.includes('_passive_') || itemName.includes('passive');
                          // Extract ALL modifier values from description using regex
                          const desc = mod.description || '';
                          const modifiers: { label: string; value: string }[] = [];
                          
                          // Regex to match stat lines like "Mining Laser Power: 115%" or "Resistance: +15%"
                          const statPattern = /([A-Za-z\s]+):\s*([+-]?\d+\.?\d*%)/g;
                          let match;
                          while ((match = statPattern.exec(desc)) !== null) {
                            let label = match[1].trim();
                            // Remove 'n' prefix from stat names (nShatter Damage -> Shatter Damage)
                            if (label.startsWith('n') && label.length > 1 && label[1] === label[1].toUpperCase()) {
                              label = label.substring(1);
                            }
                            const value = match[2];
                            // Include ALL stats (not filtering by type)
                            modifiers.push({ label, value });
                          }
                          
                          return (
                            <div key={`added-${modIdx}`} className={styles.attachmentDetails} style={{ background: 'rgba(0, 255, 100, 0.1)' }}>
                              <span className={styles.attachmentName}>
                                └ {mod.localizedName || 'Module'}
                                {isActive && <span style={{ marginLeft: '0.3rem', padding: '1px 4px', fontSize: '0.6rem', background: 'rgba(255, 170, 0, 0.2)', color: '#ffaa00', borderRadius: '2px', fontWeight: 600 }}>ACTIVE</span>}
                                {isPassive && <span style={{ marginLeft: '0.3rem', padding: '1px 4px', fontSize: '0.6rem', background: 'rgba(0, 255, 128, 0.2)', color: '#00ff80', borderRadius: '2px', fontWeight: 600 }}>PASSIVE</span>}
                              </span>
                              <div className={styles.attachmentMeta}>
                                <span className={styles.attachmentSize}>S{mod.size}</span>
                                <span className={styles.attachmentMfg}>{modParsed.manufacturer || mod.manufacturer || 'Mining Module'}</span>
                                {mod.grade && <span className={styles.attachmentGrade}>Grade {['A', 'B', 'C', 'D'][mod.grade - 1] || 'A'}</span>}
                                {!readOnly && (
                                  <button
                                    style={{ 
                                      background: 'rgba(255, 50, 50, 0.3)', 
                                      border: '1px solid rgba(255, 50, 50, 0.5)',
                                      color: '#ff6666',
                                      padding: '0 4px',
                                      borderRadius: '3px',
                                      cursor: 'pointer',
                                      fontSize: '0.65rem',
                                      marginLeft: '0.5rem'
                                    }}
                                    onClick={() => handleRemoveMiningModule(mining.hardpointId, modIdx)}
                                    title="Remove module"
                                  >
                                    ✕
                                  </button>
                                )}
                              </div>
                              {/* Show module modifier values */}
                              {modifiers.length > 0 && (
                                <div style={{ fontSize: '0.65rem', color: 'rgba(100, 255, 150, 0.8)', marginTop: '4px' }}>
                                  {modifiers.map((mod, i) => (
                                    <div key={i} style={{ marginBottom: i < modifiers.length - 1 ? '2px' : 0 }}>
                                      {mod.label}: {mod.value}
                                    </div>
                                  ))}
                                </div>
                              )}
                              {modifiers.length === 0 && (
                                <div style={{ fontSize: '0.65rem', color: 'rgba(100, 255, 150, 0.7)', marginTop: '4px', fontStyle: 'italic' }}>
                                  Performance Modifier
                                </div>
                              )}
                            </div>
                          );
                        })}
                        
                        {/* Show empty message if no modules */}
                        {totalModules === 0 && (
                          <div className={styles.attachmentDetails}>
                            <span className={styles.attachmentName} style={{ opacity: 0.5, fontStyle: 'italic' }}>
                              No modules installed
                            </span>
                          </div>
                        )}
                        
                        {/* Add Module button and dropdown - only if slots available */}
                        {!readOnly && hasAvailableSlots && (
                          <div className={styles.swapContainer} style={{ marginTop: '0.5rem' }}>
                            <button 
                              data-swap-button
                              className={`${styles.swapButton} ${isModuleDropdownOpen ? styles.swapButtonActive : ''}`}
                              style={{ fontSize: '0.75rem', padding: '0.35rem 0.75rem' }}
                              onClick={() => {
                                if (isModuleDropdownOpen) {
                                  setShowMiningModuleDropdown(false);
                                  setSelectedMiningModuleHardpoint(null);
                                } else {
                                  const laserSize = mining.miningLaser?.size || mining.slotSize || 1;
                                  loadMiningModules(mining.hardpointId, laserSize);
                                }
                              }}
                            >
                              <span>Add Module ({moduleSlots - totalModules} free)</span>
                              <ChevronDown size={12} />
                            </button>
                            
                            {isModuleDropdownOpen && (
                              <div className={`${styles.powerPlantDropdown} ${styles.miningDropdown}`} data-dropdown="miningmodule">
                                <div className={styles.dropdownHeader}>
                                  Mining Modules
                                </div>
                                {loadingMiningModules ? (
                                  <div className={styles.dropdownLoading}>
                                    <RefreshCw size={16} className={styles.spinner} />
                                    Loading...
                                  </div>
                                ) : availableMiningModules.length === 0 ? (
                                  <div className={styles.dropdownEmpty}>
                                    No mining modules found
                                  </div>
                                ) : (
                                  <div className={styles.dropdownList}>
                                    {availableMiningModules.map(mod => {
                                      const parsed = parseComponentDescription(mod.description);
                                      // Check if already added
                                      const isAlreadyAdded = addedModulesForHardpoint.some(m => m.id === mod.id);
                                      const itemName = (mod.itemName || '').toLowerCase();
                                      const isActive = itemName.includes('_active_') || itemName.includes('active');
                                      const isPassive = itemName.includes('_passive_') || itemName.includes('passive');
                                      
                                      // Extract ALL modifier stats for display
                                      const desc = mod.description || '';
                                      const modifiers: { label: string; value: string }[] = [];
                                      const statPattern = /([A-Za-z\s]+):\s*([+-]?\d+\.?\d*%)/g;
                                      let match;
                                      while ((match = statPattern.exec(desc)) !== null) {
                                        let label = match[1].trim();
                                        // Remove 'n' prefix from stat names (nShatter Damage -> Shatter Damage)
                                        if (label.startsWith('n') && label.length > 1 && label[1] === label[1].toUpperCase()) {
                                          label = label.substring(1);
                                        }
                                        const value = match[2];
                                        // Include ALL stats
                                        modifiers.push({ label, value });
                                      }
                                      
                                      return (
                                        <button
                                          key={mod.id}
                                          className={`${styles.dropdownItem} ${isAlreadyAdded ? styles.dropdownItemEquipped : ''}`}
                                          onClick={() => handleMiningModuleSelect(mod)}
                                        >
                                          <div className={styles.dropdownItemMain}>
                                            <span className={styles.dropdownItemName}>
                                              {mod.localizedName || 'Mining Module'}
                                              {isActive && <span style={{ marginLeft: '0.3rem', padding: '1px 4px', fontSize: '0.6rem', background: 'rgba(255, 170, 0, 0.3)', color: '#ffaa00', borderRadius: '2px', fontWeight: 600 }}>ACTIVE</span>}
                                              {isPassive && <span style={{ marginLeft: '0.3rem', padding: '1px 4px', fontSize: '0.6rem', background: 'rgba(0, 255, 128, 0.2)', color: '#00ff80', borderRadius: '2px', fontWeight: 600 }}>PASSIVE</span>}
                                            </span>
                                            <div className={styles.dropdownItemMeta}>
                                              <span className={styles.dropdownItemMfg}>
                                                {parsed.manufacturer || mod.manufacturer}
                                              </span>
                                              <span className={styles.dropdownItemSize}>S{mod.size}</span>
                                              <span className={styles.dropdownItemGrade}>
                                                Grade {['A', 'B', 'C', 'D'][mod.grade - 1] || '?'}
                                              </span>
                                            </div>
                                          </div>
                                          <div className={styles.dropdownItemStats}>
                                            {modifiers.slice(0, 3).map((modifier, i) => (
                                              <div key={i} style={{ fontSize: '0.7rem', color: 'rgba(100, 255, 150, 0.8)', whiteSpace: 'nowrap' }}>
                                                {modifier.label}: {modifier.value}
                                              </div>
                                            ))}
                                          </div>
                                          {isAlreadyAdded && (
                                            <Check size={16} className={styles.equippedCheck} />
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Show slots full message */}
                        {!readOnly && !hasAvailableSlots && (
                          <div style={{ fontSize: '0.7rem', color: 'rgba(255, 170, 100, 0.7)', marginTop: '0.5rem', fontStyle: 'italic' }}>
                            All {moduleSlots} module slots filled
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  
                  {/* Only show this swap button for DIRECT mining lasers (no nested miningLaser) */}
                  {!readOnly && !mining.miningLaser && (
                    <div className={styles.swapContainer}>
                      <button 
                        data-swap-button className={`${styles.swapButton} ${isSelected && showMiningDropdown ? styles.swapButtonActive : ''}`}
                        onClick={() => {
                          if (isSelected && showMiningDropdown) {
                            setShowMiningDropdown(false);
                            setSelectedMiningHardpoint(null);
                          } else {
                            loadCompatibleMiningLasers(mining.slotSize, mining.hardpointId);
                          }
                        }}
                      >
                        <span>Swap</span>
                        <ChevronDown size={14} />
                      </button>
                      
                      {showMiningDropdown && isSelected && (
                        <div className={`${styles.powerPlantDropdown} ${styles.miningDropdown}`} data-dropdown="mining">
                          <div className={styles.dropdownHeader}>
                            Size {mining.slotSize} Mining Lasers
                          </div>
                          {loadingMiningLasers ? (
                            <div className={styles.dropdownLoading}>
                              <RefreshCw size={16} className={styles.spinner} />
                              Loading...
                            </div>
                          ) : availableMiningLasers.length === 0 ? (
                            <div className={styles.dropdownEmpty}>
                              No compatible mining lasers found
                            </div>
                          ) : (
                            <div className={styles.dropdownList}>
                              {availableMiningLasers.map(laser => {
                                const isEquipped = laser.itemName === mining.itemName;
                                const parsed = parseComponentDescription(laser.description);
                                return (
                                  <button
                                    key={laser.id}
                                    className={`${styles.dropdownItem} ${isEquipped ? styles.dropdownItemEquipped : ''}`}
                                    onClick={() => handleMiningLaserSelect(laser)}
                                  >
                                    <div className={styles.dropdownItemMain}>
                                      <span className={styles.dropdownItemName}>
                                        {laser.localizedName}
                                      </span>
                                      <div className={styles.dropdownItemMeta}>
                                        <span className={styles.dropdownItemMfg}>
                                          {parsed.manufacturer || laser.manufacturer}
                                        </span>
                                        <span className={styles.dropdownItemGrade}>
                                          Grade {['A', 'B', 'C', 'D'][laser.grade - 1] || '?'}
                                        </span>
                                        {laser.health > 0 && (
                                          <span className={styles.dropdownItemHP}>
                                            {laser.health >= 1000 ? `${(laser.health / 1000).toFixed(1)}k` : laser.health} HP
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className={styles.dropdownItemStats}>
                                      <span className={styles.dropdownItemPips}>
                                        {laser.powerPipStats?.powerUnits || '?'} pips
                                      </span>
                                      {(laser.miningLaserStats?.power || laser.miningLaserStats?.resistance || laser.miningLaserStats?.instability || laser.miningLaserStats?.range) && (
                                        <div style={{ fontSize: '0.65rem', color: 'rgba(255, 255, 255, 0.5)', marginTop: '2px' }}>
                                          {laser.miningLaserStats.power && laser.miningLaserStats.power > 0 && (
                                            <span style={{ marginRight: '0.4rem' }}>{laser.miningLaserStats.power.toFixed(0)} pwr</span>
                                          )}
                                          {laser.miningLaserStats.resistance && laser.miningLaserStats.resistance > 0 && (
                                            <span style={{ marginRight: '0.4rem' }}>{laser.miningLaserStats.resistance.toFixed(1)} res</span>
                                          )}
                                          {laser.miningLaserStats.instability && laser.miningLaserStats.instability > 0 && (
                                            <span style={{ marginRight: '0.4rem' }}>{laser.miningLaserStats.instability.toFixed(1)} inst</span>
                                          )}
                                          {laser.miningLaserStats.range && laser.miningLaserStats.range > 100 && (
                                            <span>{(laser.miningLaserStats.range / 1000).toFixed(2)}km</span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    {isEquipped && (
                                      <Check size={16} className={styles.equippedCheck} />
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          <div className={styles.multiPlantNote}>
            Mining mode disables weapons when active. Toggle the MINING column to enable.
          </div>
        </div>
      ) : selectedComponent === 'tractor' && tractorInfo.length > 0 ? (
        /* TRACTOR BEAM DETAILS - shows all tractor beams with swap capability */
        <div className={styles.systemDetails}>
          <div className={styles.systemDetailsHeader}>
            <span className={`${styles.systemDetailsIcon} ${styles.tractorIcon}`}><Magnet size={20} /></span>
            <span className={styles.systemDetailsName}>TRACTOR{tractorInfo.length > 1 ? ` (${tractorInfo.length})` : ''}</span>
            <span className={styles.utilityBadge}>Utility Mode</span>
          </div>
          
          <div className={styles.pipCounter}>
            <span className={styles.pipCounterValue}>
              {tractorInfo.reduce((sum, t) => sum + t.powerUnits, 0)}
            </span>
            <span className={styles.pipCounterSeparator}>/</span>
            <span className={styles.pipCounterMax}>
              {tractorInfo.reduce((sum, t) => sum + t.powerUnits, 0)} pips total
            </span>
          </div>

          {/* Tractor Beam List with Swap Capability */}
          <div className={styles.powerPlantList}>
            {tractorInfo.map((tractor, idx) => {
              const isOverridden = hardpointOverrides?.has(tractor.hardpointId);
              const isSelected = selectedTractorHardpoint === tractor.hardpointId;
              
              return (
                <div 
                  key={tractor.hardpointId} 
                  className={`${styles.powerPlantItem} ${isOverridden ? styles.powerPlantModified : ''}`}
                >
                  <div className={styles.powerPlantHeader}>
                    <div className={styles.powerPlantSlot}>
                      <span className={styles.slotLabel}>Slot {idx + 1}</span>
                      <span className={styles.slotSize}>Size {tractor.slotSize}</span>
                    </div>
                    {isOverridden && (
                      <button 
                        className={styles.resetButton}
                        onClick={() => handleResetTractorBeam(tractor.hardpointId)}
                        title="Reset to original"
                      >
                        <RefreshCw size={14} />
                      </button>
                    )}
                  </div>
                  
                  <div className={styles.powerPlantDetails}>
                    <span className={styles.powerPlantName}>{tractor.displayName}</span>
                    <div className={styles.powerPlantMeta}>
                      <span className={styles.powerPlantMfg}>{tractor.manufacturer}</span>
                      <span className={styles.powerPlantGrade}>Grade {['A', 'B', 'C', 'D'][tractor.grade - 1] || 'C'}</span>
                    </div>
                    <div className={styles.powerPlantStats}>
                      <span className={styles.statItem}>
                        <Magnet size={12} />
                        {tractor.powerUnits} pips
                      </span>
                      {tractor.health > 0 && (
                        <span className={styles.statItem}>
                          {tractor.health >= 1000 ? `${(tractor.health / 1000).toFixed(1)}k` : tractor.health} HP
                        </span>
                      )}
                    </div>
                    {/* Tractor beam stats */}
                    {tractor.tractorStats && (tractor.tractorStats.range || tractor.tractorStats.force || tractor.tractorStats.volume) && (
                      <div style={{ fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)', marginTop: '4px' }}>
                        {tractor.tractorStats.range && tractor.tractorStats.range > 100 && (
                          <span style={{ marginRight: '0.5rem' }}>{(tractor.tractorStats.range / 1000).toFixed(2)}km</span>
                        )}
                        {tractor.tractorStats.force && tractor.tractorStats.force > 0 && (
                          <span style={{ marginRight: '0.5rem' }}>{tractor.tractorStats.force.toFixed(0)}N force</span>
                        )}
                        {tractor.tractorStats.volume && tractor.tractorStats.volume > 0 && (
                          <span>{tractor.tractorStats.volume.toFixed(1)}m³</span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {!readOnly && (
                    <div className={styles.swapContainer}>
                      <button 
                        data-swap-button className={`${styles.swapButton} ${isSelected && showTractorDropdown ? styles.swapButtonActive : ''}`}
                        onClick={() => {
                          if (isSelected && showTractorDropdown) {
                            setShowTractorDropdown(false);
                            setSelectedTractorHardpoint(null);
                          } else {
                            loadCompatibleTractorBeams(tractor.slotSize, tractor.hardpointId);
                          }
                        }}
                      >
                        <span>Swap</span>
                        <ChevronDown size={14} />
                      </button>
                      
                      {showTractorDropdown && isSelected && (
                        <div className={`${styles.powerPlantDropdown} ${styles.tractorDropdown}`} data-dropdown="tractor">
                          <div className={styles.dropdownHeader}>
                            Size {tractor.slotSize} Tractor Beams
                          </div>
                          {loadingTractorBeams ? (
                            <div className={styles.dropdownLoading}>
                              <RefreshCw size={16} className={styles.spinner} />
                              Loading...
                            </div>
                          ) : availableTractorBeams.length === 0 ? (
                            <div className={styles.dropdownEmpty}>
                              No compatible tractor beams found
                            </div>
                          ) : (
                            <div className={styles.dropdownList}>
                              {availableTractorBeams.map(beam => {
                                const isEquipped = beam.itemName === tractor.itemName;
                                const parsed = parseComponentDescription(beam.description);
                                return (
                                  <button
                                    key={beam.id}
                                    className={`${styles.dropdownItem} ${isEquipped ? styles.dropdownItemEquipped : ''}`}
                                    onClick={() => handleTractorBeamSelect(beam)}
                                  >
                                    <div className={styles.dropdownItemMain}>
                                      <span className={styles.dropdownItemName}>
                                        {beam.localizedName}
                                      </span>
                                      <div className={styles.dropdownItemMeta}>
                                        <span className={styles.dropdownItemMfg}>
                                          {parsed.manufacturer || beam.manufacturer}
                                        </span>
                                        <span className={styles.dropdownItemGrade}>
                                          Grade {['A', 'B', 'C', 'D'][beam.grade - 1] || '?'}
                                        </span>
                                        {beam.health > 0 && (
                                          <span className={styles.dropdownItemHP}>
                                            {beam.health >= 1000 ? `${(beam.health / 1000).toFixed(1)}k` : beam.health} HP
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className={styles.dropdownItemStats}>
                                      <span className={styles.dropdownItemPips}>
                                        {beam.powerPipStats?.powerUnits || '?'} pips
                                      </span>
                                      {(beam.tractorBeamStats?.range || beam.tractorBeamStats?.force || beam.tractorBeamStats?.volume) && (
                                        <div style={{ fontSize: '0.65rem', color: 'rgba(255, 255, 255, 0.5)', marginTop: '2px' }}>
                                          {beam.tractorBeamStats.range && beam.tractorBeamStats.range > 100 && (
                                            <span style={{ marginRight: '0.4rem' }}>{(beam.tractorBeamStats.range / 1000).toFixed(2)}km</span>
                                          )}
                                          {beam.tractorBeamStats.force && beam.tractorBeamStats.force > 0 && (
                                            <span style={{ marginRight: '0.4rem' }}>{beam.tractorBeamStats.force.toFixed(0)}N</span>
                                          )}
                                          {beam.tractorBeamStats.volume && beam.tractorBeamStats.volume > 0 && (
                                            <span>{beam.tractorBeamStats.volume.toFixed(1)}m³</span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    {isEquipped && (
                                      <Check size={16} className={styles.equippedCheck} />
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          <div className={styles.multiPlantNote}>
            Tractor mode disables weapons when active. Toggle the TRACTOR column to enable.
          </div>
        </div>
      ) : selectedComponent === 'salvage' && salvageInfo.length > 0 ? (
        /* SALVAGE EQUIPMENT DETAILS - salvage arms with swappable salvage heads */
        <div className={styles.systemDetails}>
          <div className={styles.systemDetailsHeader}>
            <span className={`${styles.systemDetailsIcon} ${styles.salvageIcon}`}><Cog size={20} /></span>
            <span className={styles.systemDetailsName}>SALVAGE{salvageInfo.length > 1 ? ` (${salvageInfo.length})` : ''}</span>
            <span className={styles.utilityBadge}>Utility Mode</span>
          </div>
          
          <div className={styles.pipCounter}>
            <span className={styles.pipCounterValue}>
              {salvageInfo.reduce((sum, s) => sum + s.powerUnits, 0)}
            </span>
            <span className={styles.pipCounterSeparator}>/</span>
            <span className={styles.pipCounterMax}>
              {salvageInfo.reduce((sum, s) => sum + s.powerUnits, 0)} pips total
            </span>
          </div>

          {/* Salvage Arm List with Salvage Head attachments */}
          <div className={styles.powerPlantList}>
            {salvageInfo.map((salvage, idx) => {
              // Check if this salvage head has been overridden
              const loadedItemOverrideKey = salvage.salvageHead?.loadedItemId 
                ? `${salvage.hardpointId}:${salvage.salvageHead.loadedItemId}` 
                : null;
              const isOverridden = loadedItemOverrideKey ? loadedItemOverrides?.has(loadedItemOverrideKey) : false;
              const isSelected = selectedSalvageHardpoint === salvage.hardpointId;
              
              return (
                <div 
                  key={salvage.hardpointId} 
                  className={`${styles.powerPlantItem} ${isOverridden ? styles.powerPlantModified : ''}`}
                >
                  <div className={styles.powerPlantHeader}>
                    <div className={styles.powerPlantSlot}>
                      <span className={styles.slotLabel}>Slot {idx + 1}</span>
                      <span className={styles.slotSize}>Size {salvage.slotSize}</span>
                    </div>
                    {isOverridden && salvage.salvageHead?.loadedItemId && (
                      <button 
                        className={styles.resetButton}
                        onClick={() => handleResetSalvageHead(salvage.hardpointId, salvage.salvageHead!.loadedItemId)}
                        title="Reset to original"
                      >
                        <RefreshCw size={14} />
                      </button>
                    )}
                  </div>
                  
                  <div className={styles.powerPlantDetails}>
                    <span className={styles.powerPlantName}>{salvage.displayName}</span>
                    <div className={styles.powerPlantMeta}>
                      <span className={styles.powerPlantMfg}>{salvage.manufacturer}</span>
                    </div>
                    <div className={styles.powerPlantStats}>
                      <span className={styles.statItem}>
                        <Cog size={12} />
                        {salvage.powerUnits} pips
                      </span>
                      {salvage.health > 0 && (
                        <span className={styles.statItem}>
                          {salvage.health >= 1000 ? `${(salvage.health / 1000).toFixed(1)}k` : salvage.health} HP
                        </span>
                      )}
                    </div>
                    {/* Salvage stats (controller level) */}
                    {salvage.salvageStats && (salvage.salvageStats.headCount || salvage.salvageStats.armingDistance) && (
                      <div style={{ fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)', marginTop: '4px' }}>
                        {salvage.salvageStats.headCount && salvage.salvageStats.headCount > 0 && (
                          <span style={{ marginRight: '0.5rem' }}>{salvage.salvageStats.headCount} heads</span>
                        )}
                        {salvage.salvageStats.armingDistance && salvage.salvageStats.armingDistance > 0 && (
                          <span>{salvage.salvageStats.armingDistance.toFixed(0)}m arming</span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Show salvage head attachment if present */}
                  {salvage.salvageHead && (
                    <div className={styles.attachmentSection}>
                      <div className={styles.attachmentHeader}>
                        <span className={styles.attachmentLabel}>Salvage Head:</span>
                      </div>
                      <div className={styles.attachmentDetails}>
                        <span className={styles.attachmentName}>{salvage.salvageHead.displayName}</span>
                        <div className={styles.attachmentMeta}>
                          <span className={styles.attachmentSize}>S{salvage.salvageHead.size}</span>
                          <span className={styles.attachmentMfg}>{salvage.salvageHead.manufacturer}</span>
                        </div>
                        {/* Salvage head base stats */}
                        {salvage.salvageStats && (
                          <div style={{ fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.7)', marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                            {salvage.salvageStats.headCount !== null && salvage.salvageStats.headCount !== undefined && salvage.salvageStats.headCount > 0 && (
                              <span><span style={{ color: '#ffa500', opacity: 0.8 }}>Beams:</span> {salvage.salvageStats.headCount}</span>
                            )}
                            {salvage.salvageStats.armingDistance !== null && salvage.salvageStats.armingDistance !== undefined && salvage.salvageStats.armingDistance > 0 && (
                              <span><span style={{ color: '#ffa500', opacity: 0.8 }}>Arming:</span> {salvage.salvageStats.armingDistance.toFixed(1)}m</span>
                            )}
                            {salvage.salvageStats.usesCargoGrid !== null && salvage.salvageStats.usesCargoGrid !== undefined && (
                              <span><span style={{ color: '#ffa500', opacity: 0.8 }}>Cargo Grid:</span> {salvage.salvageStats.usesCargoGrid ? 'Yes' : 'No'}</span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Swap button for salvage head */}
                      {!readOnly && (
                        <div className={styles.swapContainer}>
                          <button 
                            data-swap-button className={`${styles.swapButton} ${isSelected && showSalvageDropdown ? styles.swapButtonActive : ''}`}
                            onClick={() => {
                              if (isSelected && showSalvageDropdown) {
                                setShowSalvageDropdown(false);
                                setSelectedSalvageHardpoint(null);
                                setSelectedSalvageLoadedItemId(null);
                              } else {
                                loadCompatibleSalvageHeads(
                                  salvage.salvageHead?.size || 2, 
                                  salvage.hardpointId,
                                  salvage.salvageHead?.loadedItemId || 0
                                );
                              }
                            }}
                          >
                            <span>Swap Head</span>
                            <ChevronDown size={14} />
                          </button>
                          
                          {showSalvageDropdown && isSelected && (
                            <div className={`${styles.powerPlantDropdown} ${styles.salvageDropdown}`} data-dropdown="salvage">
                              <div className={styles.dropdownHeader}>
                                Size {salvage.salvageHead?.size || 2} Salvage Heads
                              </div>
                              {loadingSalvageHeads ? (
                                <div className={styles.dropdownLoading}>
                                  <RefreshCw size={16} className={styles.spinner} />
                                  Loading...
                                </div>
                              ) : availableSalvageHeads.length === 0 ? (
                                <div className={styles.dropdownEmpty}>
                                  No compatible salvage heads found
                                </div>
                              ) : (
                                <div className={styles.dropdownList}>
                                  {availableSalvageHeads.map(head => {
                                    const isEquipped = head.itemName === salvage.salvageHead?.itemName;
                                    const parsed = parseComponentDescription(head.description);
                                    return (
                                      <button
                                        key={head.id}
                                        className={`${styles.dropdownItem} ${isEquipped ? styles.dropdownItemEquipped : ''}`}
                                        onClick={() => handleSalvageHeadSelect(head)}
                                      >
                                        <div className={styles.dropdownItemMain}>
                                          <span className={styles.dropdownItemName}>
                                            {head.localizedName || 'Salvage Head'}
                                          </span>
                                          <div className={styles.dropdownItemMeta}>
                                            <span className={styles.dropdownItemMfg}>
                                              {parsed.manufacturer || head.manufacturer}
                                            </span>
                                            <span className={styles.dropdownItemGrade}>
                                              Grade {['A', 'B', 'C', 'D'][head.grade - 1] || '?'}
                                            </span>
                                          </div>
                                        </div>
                                        {isEquipped && (
                                          <Check size={16} className={styles.equippedCheck} />
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* If no salvage head detected, show placeholder with Add button */}
                  {!salvage.salvageHead && (
                    <div className={styles.attachmentSection}>
                      <div className={styles.attachmentHeader}>
                        <span className={styles.attachmentLabel}>Salvage Head:</span>
                      </div>
                      <div className={styles.attachmentDetails} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span className={styles.attachmentName} style={{ opacity: 0.6, fontStyle: 'italic' }}>Not equipped</span>
                        <button
                          className={styles.swapButton}
                          data-swap-button
                          style={{ marginLeft: 'auto', fontSize: '11px', padding: '2px 8px' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (selectedSalvageHardpoint === salvage.hardpointId) {
                              setSelectedSalvageHardpoint(null);
                              setShowSalvageDropdown(false);
                            } else {
                              setSelectedSalvageHardpoint(salvage.hardpointId);
                              setShowSalvageDropdown(true);
                              loadCompatibleSalvageHeads(2, salvage.hardpointId);
                            }
                          }}
                        >
                          <span>Add Head</span>
                          <ChevronDown size={12} />
                        </button>
                      </div>
                      
                      {/* Dropdown for selecting salvage head */}
                      {showSalvageDropdown && selectedSalvageHardpoint === salvage.hardpointId && (
                        <div className={`${styles.powerPlantDropdown} ${styles.salvageDropdown}`} data-dropdown="salvage">
                          <div className={styles.dropdownHeader}>
                            Compatible Salvage Heads
                          </div>
                          {loadingSalvageHeads ? (
                            <div className={styles.dropdownLoading}>
                              <RefreshCw size={16} className={styles.spinner} />
                              Loading...
                            </div>
                          ) : availableSalvageHeads.length === 0 ? (
                            <div className={styles.dropdownEmpty}>
                              No compatible salvage heads found
                            </div>
                          ) : (
                            <div className={styles.dropdownList}>
                              {availableSalvageHeads.map(head => {
                                return (
                                  <button
                                    key={head.id}
                                    className={styles.dropdownItem}
                                    onClick={() => handleSalvageHeadSelect(head)}
                                  >
                                    <div className={styles.dropdownItemMain}>
                                      <span className={styles.dropdownItemName}>{head.localizedName}</span>
                                      <span className={styles.dropdownItemMfg}>{head.manufacturer}</span>
                                    </div>
                                    <div className={styles.dropdownItemStats}>
                                      <span>Size {head.size}</span>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Salvage Modules Section - shows installed modules and Add Module button */}
                  {salvage.salvageHead && (() => {
                    const moduleSlots = salvage.salvageHead?.moduleSlots || 3;
                    const installedModules = salvage.modules || [];
                    const addedModulesForHardpoint = addedSalvageModules.get(salvage.hardpointId) || [];
                    const totalModules = installedModules.length + addedModulesForHardpoint.length;
                    const hasAvailableSlots = totalModules < moduleSlots;
                    const isModuleDropdownOpen = showSalvageModuleDropdown && selectedSalvageModuleHardpoint === salvage.hardpointId;
                    
                    return (
                      <div className={styles.attachmentSection}>
                        <div className={styles.attachmentHeader}>
                          <span className={styles.attachmentLabel}>
                            Modules ({totalModules}/{moduleSlots} slots)
                          </span>
                        </div>
                        
                        {/* Show installed modules from database */}
                        {installedModules.length > 0 && installedModules.map((mod, modIdx) => {
                          const modAny = mod as any;
                          const itemName = (modAny.itemName || '').toLowerCase();
                          const isActive = itemName.includes('_active_') || itemName.includes('active');
                          const isPassive = itemName.includes('_passive_') || itemName.includes('passive');
                          const isThisModuleSelected = selectedSalvageModuleHardpoint === salvage.hardpointId && selectedSalvageModuleForSwap === modIdx;
                          const moduleName = modAny.displayName || modAny.itemName || 'Salvage Module';
                          const moduleSize = modAny.size || 1;
                          const modParsed = parseComponentDescription(modAny.description || '');
                          const moduleMfg = modParsed.manufacturer || modAny.manufacturer || 'Unknown';
                          
                          // Extract ALL modifier values from description
                          const desc = modAny.description || '';
                          const modifiers: { label: string; value: string }[] = [];
                          // Match both percentage and non-percentage stats
                          const statPattern = /([A-Za-z\s]+):\s*([+-]?\d+\.?\d*%?[a-zA-Z]*)/g;
                          let match;
                          while ((match = statPattern.exec(desc)) !== null) {
                            let label = match[1].trim();
                            if (label.startsWith('n') && label.length > 1 && label[1] === label[1].toUpperCase()) {
                              label = label.substring(1);
                            }
                            const value = match[2];
                            modifiers.push({ label, value });
                          }
                          
                          return (
                            <div key={`db-${modIdx}`} className={styles.attachmentDetails} style={{ position: 'relative' }}>
                              <span className={styles.attachmentName}>
                                └ {moduleName}
                                {isActive && <span style={{ marginLeft: '0.3rem', padding: '1px 4px', fontSize: '0.6rem', background: 'rgba(255, 170, 0, 0.2)', color: '#ffaa00', borderRadius: '2px', fontWeight: 600 }}>ACTIVE</span>}
                                {isPassive && <span style={{ marginLeft: '0.3rem', padding: '1px 4px', fontSize: '0.6rem', background: 'rgba(0, 255, 128, 0.2)', color: '#00ff80', borderRadius: '2px', fontWeight: 600 }}>PASSIVE</span>}
                              </span>
                              <div className={styles.attachmentMeta}>
                                <span className={styles.attachmentSize}>S{moduleSize}</span>
                                <span className={styles.attachmentMfg}>{moduleMfg}</span>
                                {!readOnly && (
                                  <button
                                    data-swap-button
                                    className={`${styles.swapButton} ${isThisModuleSelected ? styles.swapButtonActive : ''}`}
                                    style={{ fontSize: '0.55rem', padding: '2px 5px', marginLeft: '0.5rem', width: 'auto', minWidth: 'auto', maxWidth: 'fit-content', flex: '0 0 auto' }}
                                    onClick={() => {
                                      if (isThisModuleSelected) {
                                        setShowSalvageModuleDropdown(false);
                                        setSelectedSalvageModuleHardpoint(null);
                                        setSelectedSalvageModuleForSwap(null);
                                      } else {
                                        setShowSalvageModuleDropdown(true);
                                        setSelectedSalvageModuleHardpoint(salvage.hardpointId);
                                        setSelectedSalvageModuleForSwap(modIdx);
                                        loadSalvageModules(salvage.hardpointId);
                                      }
                                    }}
                                  >
                                    swap
                                  </button>
                                )}
                              </div>
                              
                              {/* Show module modifier values */}
                              {modifiers.length > 0 && (
                                <div style={{ fontSize: '0.65rem', color: 'rgba(100, 255, 150, 0.8)', marginTop: '4px' }}>
                                  {modifiers.map((modifier, i) => (
                                    <div key={i} style={{ marginBottom: i < modifiers.length - 1 ? '2px' : 0 }}>
                                      {modifier.label}: {modifier.value}
                                    </div>
                                  ))}
                                </div>
                              )}
                              {modifiers.length === 0 && (
                                <div style={{ fontSize: '0.65rem', color: 'rgba(100, 200, 255, 0.7)', marginTop: '4px', fontStyle: 'italic' }}>
                                  Performance Modifier
                                </div>
                              )}
                              
                              {/* Dropdown for swapping THIS specific module */}
                              {!readOnly && isThisModuleSelected && showSalvageModuleDropdown && (
                                <div className={styles.swapContainer} style={{ marginTop: '0.5rem' }}>
                                  <div className={`${styles.powerPlantDropdown} ${styles.salvageDropdown}`} data-dropdown="salvagemodule">
                                    <div className={styles.dropdownHeader}>
                                      Salvage Modules
                                    </div>
                                    {loadingSalvageModules ? (
                                      <div className={styles.dropdownLoading}>
                                        <RefreshCw size={16} className={styles.spinner} />
                                        Loading...
                                      </div>
                                    ) : availableSalvageModules.length === 0 ? (
                                      <div className={styles.dropdownEmpty}>
                                        No salvage modules found
                                      </div>
                                  ) : (
                                    <div className={styles.dropdownList}>
                                      {availableSalvageModules.map(swapMod => {
                                        const parsed = parseComponentDescription(swapMod.description);
                                        const isEquipped = swapMod.itemName === modAny.itemName;
                                        
                                        // Extract module stats
                                        const desc = swapMod.description || '';
                                        const modifiers: { label: string; value: string }[] = [];
                                        const statPattern = /([A-Za-z\s]+):\s*([+-]?\d+\.?\d*%?[a-zA-Z]*)/g;
                                        let match;
                                        while ((match = statPattern.exec(desc)) !== null) {
                                          let label = match[1].trim();
                                          if (label.startsWith('n') && label.length > 1 && label[1] === label[1].toUpperCase()) {
                                            label = label.substring(1);
                                          }
                                          const value = match[2];
                                          modifiers.push({ label, value });
                                        }
                                        
                                        return (
                                          <button
                                            key={swapMod.id}
                                            className={`${styles.dropdownItem} ${isEquipped ? styles.dropdownItemEquipped : ''}`}
                                            onClick={() => {
                                              // Swap this specific module using onLoadedItemChange
                                              console.log('🔧 Swapping salvage module:', {
                                                hardpointId: salvage.hardpointId,
                                                moduleLoadedItemId: modAny.loadedItemId,
                                                newModuleId: swapMod.id,
                                                moduleName: modAny.displayName,
                                                newModuleName: swapMod.localizedName
                                              });
                                              if (modAny.loadedItemId && onLoadedItemChange) {
                                                // The child attachment ID is the loadedItemId, which needs to be swapped
                                                // We need the parent hardpoint ID and the child's loadedItemId
                                                onLoadedItemChange(salvage.hardpointId, modAny.loadedItemId, swapMod.id);
                                              } else {
                                                console.error('❌ Cannot swap: missing loadedItemId or onLoadedItemChange', {
                                                  hasLoadedItemId: !!modAny.loadedItemId,
                                                  hasCallback: !!onLoadedItemChange
                                                });
                                              }
                                              setShowSalvageModuleDropdown(false);
                                              setSelectedSalvageModuleHardpoint(null);
                                              setSelectedSalvageModuleForSwap(null);
                                            }}
                                          >
                                            <div className={styles.dropdownItemMain}>
                                              <span className={styles.dropdownItemName}>
                                                {swapMod.localizedName || 'Salvage Module'}
                                              </span>
                                              <div className={styles.dropdownItemMeta}>
                                                <span className={styles.dropdownItemMfg}>
                                                  {parsed.manufacturer || swapMod.manufacturer}
                                                </span>
                                                <span className={styles.dropdownItemGrade}>
                                                  Grade {['A', 'B', 'C', 'D'][swapMod.grade - 1] || '?'}
                                                </span>
                                              </div>
                                              {/* Show module stats */}
                                              {modifiers.length > 0 && (
                                                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)', marginTop: '4px' }}>
                                                  {modifiers.map((modifier, i) => (
                                                    <span key={i}><span style={{ color: '#00ff80', opacity: 0.8 }}>{modifier.label}:</span> {modifier.value}</span>
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                            {isEquipped && (
                                              <Check size={16} className={styles.equippedCheck} />
                                            )}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                        
                        {/* Show user-added modules */}
                        {addedModulesForHardpoint.map((mod, modIdx) => {
                          const itemName = (mod.itemName || '').toLowerCase();
                          const isActive = itemName.includes('_active_') || itemName.includes('active');
                          const isPassive = itemName.includes('_passive_') || itemName.includes('passive');
                          
                          return (
                            <div key={`added-${modIdx}`} className={styles.attachmentDetails} style={{ background: 'rgba(0, 255, 100, 0.1)' }}>
                              <span className={styles.attachmentName}>
                                └ {mod.localizedName || 'Module'}
                                {isActive && <span style={{ marginLeft: '0.3rem', padding: '1px 4px', fontSize: '0.6rem', background: 'rgba(255, 170, 0, 0.2)', color: '#ffaa00', borderRadius: '2px', fontWeight: 600 }}>ACTIVE</span>}
                                {isPassive && <span style={{ marginLeft: '0.3rem', padding: '1px 4px', fontSize: '0.6rem', background: 'rgba(0, 255, 128, 0.2)', color: '#00ff80', borderRadius: '2px', fontWeight: 600 }}>PASSIVE</span>}
                              </span>
                              <div className={styles.attachmentMeta}>
                                <span className={styles.attachmentMfg}>{mod.manufacturer}</span>
                                {!readOnly && (
                                  <button
                                    style={{ 
                                      background: 'rgba(255, 50, 50, 0.3)', 
                                      border: '1px solid rgba(255, 50, 50, 0.5)',
                                      color: '#ff6666',
                                      padding: '0 4px',
                                      borderRadius: '3px',
                                      cursor: 'pointer',
                                      fontSize: '0.65rem',
                                      marginLeft: '0.5rem'
                                    }}
                                    onClick={() => handleRemoveSalvageModule(salvage.hardpointId, modIdx)}
                                    title="Remove module"
                                  >
                                    ✕
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Show empty message if no modules */}
                        {totalModules === 0 && (
                          <div className={styles.attachmentDetails}>
                            <span className={styles.attachmentName} style={{ opacity: 0.5, fontStyle: 'italic' }}>
                              No modules installed
                            </span>
                          </div>
                        )}
                        
                        {/* Add Module button and dropdown - only if slots available */}
                        {!readOnly && hasAvailableSlots && (
                          <div className={styles.swapContainer} style={{ marginTop: '0.5rem' }}>
                            <button 
                              data-swap-button
                              className={`${styles.swapButton} ${isModuleDropdownOpen ? styles.swapButtonActive : ''}`}
                              style={{ fontSize: '0.75rem', padding: '0.35rem 0.75rem' }}
                              onClick={() => {
                                if (isModuleDropdownOpen) {
                                  setShowSalvageModuleDropdown(false);
                                  setSelectedSalvageModuleHardpoint(null);
                                } else {
                                  loadSalvageModules(salvage.hardpointId);
                                }
                              }}
                            >
                              <span>Add Module ({moduleSlots - totalModules} free)</span>
                              <ChevronDown size={12} />
                            </button>
                            
                            {isModuleDropdownOpen && (
                              <div className={`${styles.powerPlantDropdown} ${styles.salvageDropdown}`} data-dropdown="salvagemodule">
                                <div className={styles.dropdownHeader}>
                                  Salvage Modules
                                </div>
                                {loadingSalvageModules ? (
                                  <div className={styles.dropdownLoading}>
                                    <RefreshCw size={16} className={styles.spinner} />
                                    Loading...
                                  </div>
                                ) : availableSalvageModules.length === 0 ? (
                                  <div className={styles.dropdownEmpty}>
                                    No salvage modules found
                                  </div>
                                ) : (
                                  <div className={styles.dropdownList}>
                                    {availableSalvageModules.map(mod => {
                                      const parsed = parseComponentDescription(mod.description);
                                      // Check if already added
                                      const isAlreadyAdded = addedModulesForHardpoint.some(m => m.id === mod.id);
                                      const itemName = (mod.itemName || '').toLowerCase();
                                      const isActive = itemName.includes('_active_') || itemName.includes('active');
                                      const isPassive = itemName.includes('_passive_') || itemName.includes('passive');
                                      
                                      // Extract module stats
                                      const desc = mod.description || '';
                                      const modifiers: { label: string; value: string }[] = [];
                                      const statPattern = /([A-Za-z\s]+):\s*([+-]?\d+\.?\d*%?[a-zA-Z]*)/g;
                                      let match;
                                      while ((match = statPattern.exec(desc)) !== null) {
                                        let label = match[1].trim();
                                        if (label.startsWith('n') && label.length > 1 && label[1] === label[1].toUpperCase()) {
                                          label = label.substring(1);
                                        }
                                        const value = match[2];
                                        modifiers.push({ label, value });
                                      }
                                      
                                      return (
                                        <button
                                          key={mod.id}
                                          className={`${styles.dropdownItem} ${isAlreadyAdded ? styles.dropdownItemEquipped : ''}`}
                                          onClick={() => handleSalvageModuleSelect(mod)}
                                        >
                                          <div className={styles.dropdownItemMain}>
                                            <span className={styles.dropdownItemName}>
                                              {mod.localizedName || 'Salvage Module'}
                                              {isActive && <span style={{ marginLeft: '0.3rem', padding: '1px 4px', fontSize: '0.6rem', background: 'rgba(255, 170, 0, 0.3)', color: '#ffaa00', borderRadius: '2px', fontWeight: 600 }}>ACTIVE</span>}
                                              {isPassive && <span style={{ marginLeft: '0.3rem', padding: '1px 4px', fontSize: '0.6rem', background: 'rgba(0, 255, 128, 0.2)', color: '#00ff80', borderRadius: '2px', fontWeight: 600 }}>PASSIVE</span>}
                                            </span>
                                            <div className={styles.dropdownItemMeta}>
                                              <span className={styles.dropdownItemMfg}>
                                                {parsed.manufacturer || mod.manufacturer}
                                              </span>
                                              <span className={styles.dropdownItemGrade}>
                                                Grade {['A', 'B', 'C', 'D'][mod.grade - 1] || '?'}
                                              </span>
                                            </div>
                                            {/* Show module stats */}
                                            {modifiers.length > 0 && (
                                              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)', marginTop: '4px' }}>
                                                {modifiers.map((modifier, i) => (
                                                  <span key={i}><span style={{ color: '#00ff80', opacity: 0.8 }}>{modifier.label}:</span> {modifier.value}</span>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                          {isAlreadyAdded && (
                                            <Check size={16} className={styles.equippedCheck} />
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Show slots full message */}
                        {!readOnly && !hasAvailableSlots && (
                          <div style={{ fontSize: '0.7rem', color: 'rgba(255, 170, 100, 0.7)', marginTop: '0.5rem', fontStyle: 'italic' }}>
                            All {moduleSlots} module slots filled
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
          
          <div className={styles.multiPlantNote}>
            Salvage arms are ship-integrated. Salvage heads can be swapped. Toggle the SALVAGE column to enable salvage mode.
          </div>
        </div>
      ) : selectedComponentData?.type === 'weapon' && weaponContent ? (
        /* WEAPON DETAILS - render custom content passed via prop */
        <div className={styles.systemDetails}>
          <div className={styles.systemDetailsHeader}>
            <span className={`${styles.systemDetailsIcon} ${styles.weaponIcon}`}><Crosshair size={20} /></span>
            <span className={styles.systemDetailsName}>WEAPONS</span>
            <span className={styles.scmBadge}>SCM Only</span>
          </div>
          <p className={styles.systemDescription}>
            Gun capacitor regen & fire rate
          </p>
          <div className={styles.pipCounter}>
            <span className={styles.pipCounterValue}>
              {selectedComponentData.isOn ? selectedComponentData.allocatedPips : 0}
            </span>
            <span className={styles.pipCounterSeparator}>/</span>
            <span className={styles.pipCounterMax}>{selectedComponentData.maxPips} pips</span>
          </div>
          {/* Weapon selection UI passed via prop */}
          <div className={styles.weaponContentWrapper}>
            {weaponContent}
          </div>
        </div>
      ) : selectedComponentData && (
        <div className={styles.systemDetails}>
          <div className={styles.systemDetailsHeader}>
            <span className={styles.systemDetailsIcon}>{selectedComponentData.icon}</span>
            <span className={styles.systemDetailsName}>{selectedComponentData.name}</span>
            {selectedComponentData.scmOnly && (
              <span className={styles.scmBadge}>SCM Only</span>
            )}
            {selectedComponentData.navOnly && (
              <span className={styles.navBadge}>NAV Only</span>
            )}
          </div>
          <p className={styles.systemDescription}>
            {selectedComponentData.type === 'weapon' && 'Gun capacitor regen & fire rate'}
            {selectedComponentData.type === 'shield' && 'Shield strength & regen rate'}
            {selectedComponentData.type === 'radar' && 'Detection range & scan resolution'}
            {selectedComponentData.type === 'lifesupport' && 'Oxygen generation & gravity'}
            {selectedComponentData.type === 'flightcontroller' && 'Thruster power & acceleration'}
          </p>
          {selectedComponentData.type !== 'quantum' && (
            <>
              <div className={styles.pipCounter}>
                <span className={styles.pipCounterValue}>
                  {selectedComponentData.isOn ? selectedComponentData.allocatedPips : 0}
                </span>
                <span className={styles.pipCounterSeparator}>/</span>
                <span className={styles.pipCounterMax}>{selectedComponentData.maxPips} pips</span>
              </div>
              {selectedComponentData.minPips > 0 && (
                <div className={styles.minPipsNote}>
                  Minimum {selectedComponentData.minPips} pip{selectedComponentData.minPips > 1 ? 's' : ''} required to operate
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PowerManagementPanel;
